import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Card } from './components/UI';
import MatchingInfographic from './components/MatchingInfographic';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { useTranslation } from './i18n';

const WEREPRESENT_LINKS = {
    privacy: 'https://werepresent.nl/privacy',
    voorwaarden: 'https://werepresent.nl/voorwaarden',
    aiPolicy: 'https://werepresent.nl/ai-policy',
};

// Pilot mode: contracts are handled manually outside the platform.
const CONTRACT_MODAL_DISABLED = true;
const MONITORING_MODAL_DISABLED = true;

// De vier intake-onderdelen die los van elkaar opnieuw doorlopen kunnen worden.
const PROFILE_SECTIONS = ['IDENTITY', 'AMBITION', 'SKILLS', 'CONDITIONS'];

function isConstraintActive(value) {
    if (typeof value === 'number') return value >= 2;
    return Boolean(value);
}

function getConstraintLabel(t, key) {
    const translated = t(`constraints.items.${key}`);
    // translate() geeft de sleutel terug wanneer die ontbreekt; val dan terug op de ruwe key.
    return translated === `constraints.items.${key}` ? key.replace(/_/g, ' ') : translated;
}

function getConstraintImportance(t, value) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
        if (parsed >= 3) return t('constraints.importance.very_important');
        if (parsed >= 2) return t('constraints.importance.important');
        return t('constraints.importance.less_important');
    }
    return value ? t('constraints.importance.important') : t('constraints.importance.less_important');
}

// --- FORMATTED TEXT HELPER ---
// Formatteert lange teksten in leesbare paragrafen met kopjes
function FormattedText({ text, className = "" }) {
  if (!text) return null;
  
  // Verwerk de tekst: splits op dubbele newlines of detecteer structuur
  const paragraphs = text
    .split(/\n\n+|\r\n\r\n+/)
    .map(p => p.trim())
    .filter(p => p.length > 0);
  
  return (
    <div className={`space-y-3 ${className}`}>
      {paragraphs.map((paragraph, idx) => {
        // Detecteer kopjes: eindigt op dubbelpunt of is kort en bold-achtig
        const isHeading = /^[A-Z][^.!?]*:$/.test(paragraph) || 
                          (paragraph.length < 60 && paragraph.endsWith(':'));
        
        // Detecteer bullet points
        if (paragraph.includes('\n- ') || paragraph.startsWith('- ')) {
          const lines = paragraph.split('\n');
          const title = lines[0].endsWith(':') ? lines[0] : null;
          const bullets = lines.filter(l => l.trim().startsWith('- '));
          
          return (
            <div key={idx}>
              {title && <p className="font-semibold text-gray-800 mb-1">{title}</p>}
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                {bullets.map((b, i) => (
                  <li key={i}>{b.replace(/^-\s*/, '')}</li>
                ))}
              </ul>
            </div>
          );
        }
        
        if (isHeading) {
          return (
            <h4 key={idx} className="font-semibold text-gray-800 mt-2 first:mt-0">
              {paragraph}
            </h4>
          );
        }
        
        return (
          <p key={idx} className="text-gray-700 leading-relaxed">
            {paragraph}
          </p>
        );
      })}
    </div>
  );
}

// --- CHAOS / TORN LABEL HELPERS (kandidaat landing-stijl) ---
// Compacte rauwe label met clip-path, rotatie en huiskleuren (groen/paars/dark/cream)
function TornBadge({ children, variant = 'dark', rotate = '-rotate-1', size = 'sm', className = '' }) {
    const palette = {
        green: 'bg-[#3f7f65] text-white',
        purple: 'bg-[#713d8d] text-white',
        dark: 'bg-[#151827] text-white',
        cream: 'bg-[#fffdf8] text-[#151827]',
        white: 'bg-white text-[#151827]',
    };
    const sizing = size === 'lg'
        ? 'px-5 py-2.5 text-sm sm:text-base'
        : size === 'md'
            ? 'px-4 py-2 text-xs sm:text-sm'
            : 'px-3 py-1.5 text-[11px] sm:text-xs';
    return (
        <span
            className={`inline-block ${rotate} ${palette[variant]} ${sizing} font-black uppercase tracking-tight shadow-xl ${className}`}
            style={{ clipPath: 'polygon(0 13%, 94% 0, 100% 82%, 7% 100%)' }}
        >
            {children}
        </span>
    );
}

// Forse rotated kop met dikke rand en offset-schaduw, voor sectie-titels
function ChaosTitle({ children, variant = 'dark', rotate = '-rotate-1', className = '' }) {
    const palette = {
        green: 'bg-[#3f7f65] text-white',
        purple: 'bg-[#713d8d] text-white',
        dark: 'bg-[#151827] text-white',
        cream: 'bg-[#fffdf8] text-[#151827]',
    };
    return (
        <span
            className={`inline-block ${rotate} border-[3px] border-[#151827] ${palette[variant]} px-4 py-1.5 text-base sm:text-lg font-black uppercase tracking-tight shadow-[6px_6px_0_#151827] ${className}`}
        >
            {children}
        </span>
    );
}

export function Dashboard({ user, onLogout, onResumeOnboarding, apiBase, onBackToHome }) {
    const { t, language } = useTranslation();
  const isCompany = user.type === 'company';
    const useUnifiedStyle = true;

    useEffect(() => {
        if (!isCompany) {
            return undefined;
        }

        const previousTitle = document.title;
        document.title = t('route_titles.business');

        return () => {
            document.title = previousTitle;
        };
    }, [isCompany, t]);
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // NIEUW: State voor het profiel venster
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showMonitoringModal, setShowMonitoringModal] = useState(false);
  const [showMatchesModal, setShowMatchesModal] = useState(false); // NIEUW
  const [showPasswordModal, setShowPasswordModal] = useState(false); // Wachtwoord wijzigen
  const [showAccountSettingsModal, setShowAccountSettingsModal] = useState(false); // AVG Account Settings
  const [showContactModal, setShowContactModal] = useState(false); // Contact modal
  const [fullProfile, setFullProfile] = useState(null);
  const [fullProfileLanguage, setFullProfileLanguage] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  const [showContractModal, setShowContractModal] = useState(false);
  const [revisionTarget, setRevisionTarget] = useState(null);
  const [revisionBusy, setRevisionBusy] = useState(false);

  // Fetch status (bestaande code)
  useEffect(() => {
    let isMounted = true;
    async function fetchStatus() {
      try {
        // Roep het juiste endpoint aan op basis van user type
        const endpoint = isCompany 
          ? `${apiBase}/api/company/status?companyId=${encodeURIComponent(user.id)}`
          : `${apiBase}/api/candidate/status?candidateId=${encodeURIComponent(user.id)}`;
        
        const res = await fetch(endpoint);
        if (res.ok) {
            const data = await res.json();
            if (isMounted) setStatus(data);
        }
      } catch (e) {
                console.error("Failed to fetch status:", e);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchStatus();
    return () => { isMounted = false };
  }, [apiBase, user.id, isCompany]);

  // NIEUW: Functie om het volledige profiel op te halen voor de modal
  const handleViewProfile = async () => {
    setShowProfileModal(true);
    // Alleen hergebruiken als we 'm al hebben opgehaald in de HUIDIGE taal —
    // anders blijven AI-analyses en ESCO-labels na een taalwissel op de oude
    // taal staan tot een volledige refresh/re-login (zie taalwissel-bug).
    if (fullProfile && fullProfileLanguage === language) return;

    setLoadingProfile(true);
    try {
        // Roep het juiste dump endpoint aan op basis van user type
        const endpoint = isCompany 
          ? `${apiBase}/api/company/dump?companyId=${encodeURIComponent(user.id)}`
          : `${apiBase}/api/candidate/dump?candidateId=${encodeURIComponent(user.id)}`;
        
        const res = await fetch(endpoint);
        console.log("Profile fetch response:", res);
        const data = await res.json();
        
        // Sla de hele blob op
        setFullProfile(data);
        setFullProfileLanguage(language);
    } catch (e) {
        console.error("Failed to load profile:", e);
        alert(t('dashboard.profile_load_failed'));
        setShowProfileModal(false);
    } finally {
        setLoadingProfile(false);
    }
  };

    const handleRegenerateTrialPeriodExamples = async () => {
        setLoadingProfile(true);
        try {
            const regenerateRes = await fetch(`${apiBase}/api/company/trial-period/regenerate?companyId=${encodeURIComponent(user.id)}`, {
                method: 'POST',
            });
            if (!regenerateRes.ok) {
                const txt = await regenerateRes.text();
                throw new Error(txt || `HTTP ${regenerateRes.status}`);
            }

            const endpoint = isCompany
                ? `${apiBase}/api/company/dump?companyId=${encodeURIComponent(user.id)}`
                : `${apiBase}/api/candidate/dump?candidateId=${encodeURIComponent(user.id)}`;
            const res = await fetch(endpoint);
            const data = await res.json();

            setFullProfile(data);
            setFullProfileLanguage(language);
        } catch (e) {
            console.error('Failed to regenerate trial period examples:', e);
            alert(t('common.unknown_error'));
        } finally {
            setLoadingProfile(false);
        }
    };

  const progress = status?.progress || 0;
  // Vertaal interne fase-namen naar gebruiksvriendelijke labels
    const getPhaseLabel = (phase) => {
        if (!phase) return t('dashboard.phase_start');
    const upperPhase = phase.toUpperCase();
        if (upperPhase === "COMPLETED") return t('dashboard.phase_active');
        if (upperPhase === "START") return t('dashboard.phase_start');
    // Alle intake fases (SEARCH, INTERVIEW, VACANCIES, CONDITIONS, IDENTITY, AMBITION, OCCUPATION, SKILLS, CONSTRAINTS)
        return t('dashboard.phase_intake');
  };
  const phaseLabel = getPhaseLabel(status?.phase);
  const isOnboardingComplete = progress === 100;
        const onboardingActionLabel = progress > 0
            ? t('dashboard.resume_onboarding_with_progress', { progress })
            : t('dashboard.start_onboarding');

    const dashboardGuidanceI18n = isCompany
        ? isOnboardingComplete
            ? {
                    title: t('dashboard.guidance_company_complete_title'),
                    body: t('dashboard.guidance_company_complete_body'),
                    action: t('dashboard.guidance_company_complete_action')
                }
            : {
                    title: t('dashboard.guidance_company_incomplete_title'),
                    body: t('dashboard.guidance_company_incomplete_body'),
                    action: t('dashboard.guidance_company_incomplete_action', { onboardingActionLabel })
                }
        : isOnboardingComplete
            ? {
                    title: t('dashboard.guidance_candidate_complete_title'),
                    body: t('dashboard.guidance_candidate_complete_body'),
                    action: t('dashboard.guidance_candidate_complete_action')
                }
            : {
                    title: t('dashboard.guidance_candidate_incomplete_title'),
                    body: t('dashboard.guidance_candidate_incomplete_body'),
                    action: t('dashboard.guidance_candidate_incomplete_action', { onboardingActionLabel })
                };
  // Header: voor companies de bedrijfsnaam, voor candidates de persoonsnaam
  const headerDisplayName = isCompany 
    ? (status?.personalia?.company_name || user.personalia?.company_name || user.username)
    : (status?.personalia?.name || user.personalia?.name || user.username);
  // Welkom bericht: altijd de naam van de contactpersoon
  const welcomeName = status?.personalia?.name || user.personalia?.name || user.username;

  const handleOpenMonitoring = () => {
        if (MONITORING_MODAL_DISABLED || !isOnboardingComplete) return; // Dubbele check
    setShowMonitoringModal(true);
  };

  const revisionUrl = (action) => (isCompany
    ? `${apiBase}/api/company/revision/${action}?companyId=${encodeURIComponent(user.id)}`
    : `${apiBase}/api/candidate/revision/${action}?candidateId=${encodeURIComponent(user.id)}`);

  const activeRevision = status?.revision || null;
  const staleSections = status?.stale_sections || [];

  const handleConfirmRevision = async () => {
    if (!revisionTarget) return;
    setRevisionBusy(true);
    try {
      const res = await fetch(revisionUrl('start'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section: revisionTarget }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || t('revision.start_failed'));
        return;
      }
      setRevisionTarget(null);
      onResumeOnboarding();
    } catch (e) {
      console.error('Failed to start revision:', e);
      alert(t('revision.start_failed'));
    } finally {
      setRevisionBusy(false);
    }
  };

  const handleCancelRevision = async () => {
    setRevisionBusy(true);
    try {
      const res = await fetch(revisionUrl('cancel'), { method: 'POST' });
      if (res.ok) setStatus({ ...status, revision: null });
    } catch (e) {
      console.error('Failed to cancel revision:', e);
    } finally {
      setRevisionBusy(false);
    }
  };

  if (loading) {
      return <div className="min-h-screen flex items-center justify-center text-gray-400">{t('dashboard.loading_status')}</div>;
  }

    return (
        <div className="relative min-h-screen overflow-hidden bg-[#f6f1e8] font-sans text-gray-800">
            <>
                <div
                    className="pointer-events-none absolute inset-0 opacity-[0.10]"
                    style={{
                        backgroundImage: 'radial-gradient(#151827 1px, transparent 1px)',
                        backgroundSize: '18px 18px',
                    }}
                />
                <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-[#3f7f65] opacity-25 blur-3xl" />
                <div className="pointer-events-none absolute -right-24 top-8 h-[28rem] w-[28rem] rounded-full bg-[#713d8d] opacity-25 blur-3xl" />
            </>
      {/* HEADER */}
            <header className="sticky top-0 z-10 border-b-4 border-[#151827] bg-white/95 shadow-sm backdrop-blur safe-top">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            {onBackToHome && (
              <button 
                onClick={onBackToHome}
                                className="p-2 rounded-lg text-brand-green transition-colors hover:bg-gray-100 flex-shrink-0"
                                title={t('dashboard.back_to_site')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
              </button>
            )}
            <div className="flex items-center gap-2 min-w-0">
                            <img src="/logozondertekst.png" alt={isCompany ? 'werepresenttalent.nl' : 'Betaaldsolliciteren.nl'} className="h-6 sm:h-8 w-auto flex-shrink-0" />
                            <span className="hidden text-base font-black tracking-tight text-[#3f7f65] sm:block sm:text-xl">{isCompany ? 'werepresenttalent.nl' : 'Betaaldsolliciteren.nl'}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <div className="text-right hidden md:block">
              <div className="text-sm font-bold text-gray-900 truncate max-w-[150px]">{headerDisplayName}</div>
                            <div className="text-xs text-gray-500 uppercase">{isCompany ? t('dashboard.organization') : t('dashboard.candidate')}</div>
            </div>
            <LanguageSwitcher
              className="hidden sm:block"
              onPersist={async (nextLang) => {
                // Sync naar backend zodat AI-prompts, emails en toekomstige
                // sessies dezelfde taal gebruiken. Zonder id (bv. onbekende
                // user) slaan we het API-call over — localStorage volstaat.
                const userId = user?.id;
                if (!userId) return;
                const path = isCompany
                  ? `/api/company/settings/language?companyId=${encodeURIComponent(userId)}`
                  : `/api/candidate/settings/language?candidateId=${encodeURIComponent(userId)}`;
                await fetch(`${apiBase}${path}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ language: nextLang }),
                });
              }}
            />
            <button 
              onClick={() => setShowAccountSettingsModal(true)} 
              className="p-2 text-gray-500 hover:text-brand-green touch-target"
                            title={t('dashboard.account_privacy')}
            >
              ⚙️
            </button>
            <button 
              onClick={() => setShowPasswordModal(true)} 
              className="p-2 text-gray-500 hover:text-brand-green touch-target hidden sm:block"
                            title={t('dashboard.change_password')}
            >
              🔐
            </button>
            <button onClick={onLogout} className="text-xs sm:text-sm text-red-500 hover:text-red-700 font-medium touch-target">
                            <span className="hidden sm:inline">{t('common.logout')}</span>
              <span className="sm:hidden">↪️</span>
            </button>
          </div>
        </div>
      </header>

    <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* HERO SECTION */}
        <section>
                                        <div className="flex flex-col items-center justify-between rounded-2xl border-4 border-[#151827] bg-white p-8 text-[#151827] shadow-[12px_12px_0_#151827] md:flex-row">
            <div>
                            <h1 className="mb-2 text-3xl font-black">
                                {t('dashboard.welcome', { name: welcomeName.split(' ')[0] })}!
              </h1>
                            <p className="max-w-xl text-[#2a2f40]">
                {!isOnboardingComplete 
                                    ? t('dashboard.profile_incomplete') 
                                    : t('dashboard.profile_active')}
              </p>
            </div>
            
            <div className="mt-6 md:mt-0">
              {!isOnboardingComplete ? (
                <button 
                  onClick={onResumeOnboarding}
                  className="rounded-xl bg-[#3f7f65] px-6 py-3 font-black text-white transition hover:translate-x-0.5 hover:translate-y-0.5"
                  style={{ boxShadow: '8px 8px 0 #151827' }}
                >
                                    {progress > 0
                                        ? `${t('dashboard.resume_onboarding')} (${progress}%) ➤`
                                        : `${t('dashboard.start_onboarding')} ➤`}
                </button>
              ) : (
                // AANGEPAST: Knop opent nu de modal
                <button 
                  onClick={handleViewProfile}
                                    className="rounded-xl border-2 border-[#151827] bg-[#fffdf8] px-6 py-3 font-black text-[#151827] transition hover:bg-white"
                >
                                    {t('dashboard.view_profile')}
                </button>
              )}
            </div>
          </div>
        </section>

                {/* WIDGETS GRID */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <Card className="col-span-1 border-4 border-[#151827] bg-white/90 shadow-[8px_8px_0_#151827]">
                            <div className="mb-5 flex flex-wrap items-center gap-3">
                                                                <ChaosTitle variant="dark" rotate="-rotate-2">{t('dashboard.status')}</ChaosTitle>
                                <TornBadge variant="green" rotate="rotate-2" size="md">{phaseLabel}</TornBadge>
                            </div>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between text-sm">
                                                                <span>{t('dashboard.progress')}</span>
                                                                <span className={isOnboardingComplete ? 'text-green-600 font-bold' : 'text-[#3f7f65] font-bold'}>
                                    {progress}%
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div 
                                                                        className="h-2.5 rounded-full bg-gradient-to-r from-[#3f7f65] to-[#713d8d] transition-all duration-1000 ease-out"
                                        style={{ width: `${progress}%` }}
                                ></div>
                            </div>
                        </div>
                    </Card>

                                        <Card className="col-span-1 border-4 border-[#151827] bg-white/90 shadow-[8px_8px_0_#151827] md:col-span-2">
                            <div className="mb-5 flex flex-wrap items-center gap-3">
                                                                <ChaosTitle variant="purple" rotate="-rotate-1">{t('dashboard.actions')}</ChaosTitle>
                                <span className="text-2xl font-black text-[#151827]/60">&amp;</span>
                                                                <ChaosTitle variant="green" rotate="rotate-2">{t('dashboard.insights')}</ChaosTitle>
                            </div>
                        <div className="grid grid-cols-2 gap-4">
                             <DashboardButton 
                                        icon="📄" 
                                                                                title={t('dashboard.contracts')} 
                                                                                desc={t('dashboard.view_and_sign')} 
                                                                                disabled={CONTRACT_MODAL_DISABLED || !isOnboardingComplete}
                                                                                onClick={() => {
                                                                                        if (!CONTRACT_MODAL_DISABLED) setShowContractModal(true);
                                                                                }}
                                                                                isCompany={useUnifiedStyle ? false : isCompany}
                                                                                accent="green"
                                                                                rotate="-rotate-1"
                                />
                             <DashboardButton 
                                        icon="🔍" 
                                title={t('dashboard.opportunities')} 
                                desc={t('dashboard.open_matches')} 
                                        disabled={!isOnboardingComplete}
                                        onClick={() => setShowMatchesModal(true)}
                                                                                isCompany={useUnifiedStyle ? false : isCompany}
                                                                                accent="purple"
                                                                                rotate="rotate-1"
                                />
                             <DashboardButton 
                                        icon="💬" 
                                title={t('dashboard.messages')} 
                                desc={t('dashboard.contact')} 
                                        onClick={() => setShowContactModal(true)}
                                                                                isCompany={useUnifiedStyle ? false : isCompany}
                                                                                accent="dark"
                                                                                rotate="rotate-2"
                                />
                             {/* DE MONITORING KNOP */}
                             <button 
                                        onClick={handleOpenMonitoring}
                                                                                disabled={MONITORING_MODAL_DISABLED || !isOnboardingComplete}
                                                                                className={`flex items-start rounded-lg border p-4 text-left transition-all ${MONITORING_MODAL_DISABLED || !isOnboardingComplete ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400' : 'border-2 border-[#151827]/35 bg-[#fffdf8] hover:border-[#3f7f65] hover:bg-white'}`}
                                >
                                    <div className="w-full">
                                                                                <TornBadge variant="green" rotate="-rotate-2" size="md" className="mb-2">{t('dashboard.monitoring')}</TornBadge>
                                                                                <div className="break-words text-xs text-[#151827]/60">{t('dashboard.log_hours_experience')}</div>
                                    </div>
                                </button>
                        </div>
                    </Card>
                </div>

                                <section>
                                        <Card className="border-4 border-[#151827] bg-white/95 shadow-[8px_8px_0_#151827]">
                                                <div className="mb-3 flex flex-wrap items-center gap-3">
                                                </div>
                                                <h3 className="text-lg font-black text-[#151827]">{dashboardGuidanceI18n.title}</h3>
                                                <p className="mt-2 text-sm leading-relaxed text-[#2a2f40]">{dashboardGuidanceI18n.body}</p>
                                                <p className="mt-3 text-sm font-semibold text-[#3f7f65]">{dashboardGuidanceI18n.action}</p>
                                        </Card>
                                </section>

        {/* PROFIELONDERDELEN OPNIEUW DOORLOPEN */}
        {(isOnboardingComplete || activeRevision) && (
          <section>
            <Card className="border-4 border-[#151827] bg-white/95 shadow-[8px_8px_0_#151827]">
              <h3 className="text-lg font-black text-[#151827]">{t('revision.section_title')}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#2a2f40]">{t('revision.section_intro')}</p>

              {activeRevision ? (
                <div className="mt-4 rounded-xl border-2 border-[#3f7f65] bg-[#eef6f2] p-4">
                  <p className="text-sm font-bold text-[#151827]">
                    {t('revision.in_progress', { section: t(`revision.sections.${activeRevision.section}`) })}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button
                      onClick={onResumeOnboarding}
                      className="rounded-xl bg-[#3f7f65] px-4 py-2 text-sm font-black text-white transition hover:translate-x-0.5 hover:translate-y-0.5"
                      style={{ boxShadow: '4px 4px 0 #151827' }}
                    >
                      {t('revision.continue')} ➤
                    </button>
                    <button
                      onClick={handleCancelRevision}
                      disabled={revisionBusy}
                      className="rounded-xl border-2 border-[#151827] bg-white px-4 py-2 text-sm font-bold text-[#151827] transition hover:bg-gray-50 disabled:opacity-50"
                    >
                      {t('revision.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {PROFILE_SECTIONS.map((section) => (
                    <button
                      key={section}
                      onClick={() => setRevisionTarget(section)}
                      className="rounded-xl border-2 border-[#151827] bg-[#fffdf8] p-4 text-left transition hover:bg-white"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-black text-[#151827]">{t(`revision.sections.${section}`)}</span>
                        <span className="text-xs font-bold text-[#3f7f65]">{t('revision.redo')} ➤</span>
                      </div>
                      {staleSections.includes(section) && (
                        <p className="mt-2 text-xs font-semibold text-[#a35a1f]">⚠ {t('revision.stale_hint')}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </Card>
          </section>
        )}

                {/* HOE WERKT HET */}
                <MatchingInfographic audience={isCompany ? 'company' : 'candidate'} />
      </main>

      {/* NIEUW: PROFIEL MODAL */}
      {showProfileModal && (
        <ProfileModal 
            data={fullProfile} 
            loading={loadingProfile} 
            onClose={() => setShowProfileModal(false)}
            isCompany={isCompany}
            onRegenerateTrialPeriodExamples={handleRegenerateTrialPeriodExamples}
        />
      )}

      {revisionTarget && (
        <RevisionConfirmModal
            section={revisionTarget}
            busy={revisionBusy}
            onConfirm={handleConfirmRevision}
            onClose={() => setRevisionTarget(null)}
        />
      )}

            {showMonitoringModal && !MONITORING_MODAL_DISABLED && (
        <MonitoringModal 
            user={user} 
            apiBase={apiBase} 
                        isCompany={isCompany}
            onClose={() => setShowMonitoringModal(false)}
        />
      )}

            {showContractModal && !CONTRACT_MODAL_DISABLED && (
        <ContractModal 
            user={user} 
            apiBase={apiBase} 
                        isCompany={isCompany}
            onClose={() => setShowContractModal(false)} 
        />
      )}

      {showMatchesModal && (
        <MatchesModal 
            user={user} 
            apiBase={apiBase} 
            isCompany={isCompany}
            onClose={() => setShowMatchesModal(false)} 
        />
      )}

      {showPasswordModal && (
        <ChangePasswordModal 
            user={user} 
            apiBase={apiBase} 
            isCompany={isCompany}
            onClose={() => setShowPasswordModal(false)} 
        />
      )}

      {showAccountSettingsModal && (
        <AccountSettingsModal 
            user={user} 
            apiBase={apiBase} 
            isCompany={isCompany}
            onClose={() => setShowAccountSettingsModal(false)}
            onLogout={onLogout}
        />
      )}

            {showContactModal && (
        <ContactModal 
            user={user} 
            apiBase={apiBase} 
            isCompany={isCompany}
            onClose={() => setShowContactModal(false)}
        />
      )}

    </div>
  );
}

// --- HELPER COMPONENTS ---

function RevisionConfirmModal({ section, busy, onConfirm, onClose }) {
    const { t } = useTranslation();
    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-lg rounded-2xl border-4 border-[#151827] bg-white p-6 shadow-[12px_12px_0_#151827]">
                <h3 className="text-xl font-black text-[#151827]">
                    {t('revision.confirm_title', { section: t(`revision.sections.${section}`) })}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#2a2f40]">{t('revision.confirm_body')}</p>
                <p className="mt-2 text-sm leading-relaxed text-[#2a2f40]">{t('revision.confirm_note')}</p>
                <div className="mt-6 flex flex-wrap justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="rounded-xl border-2 border-[#151827] bg-white px-4 py-2 text-sm font-bold text-[#151827] transition hover:bg-gray-50 disabled:opacity-50"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={busy}
                        className="rounded-xl bg-[#3f7f65] px-5 py-2 text-sm font-black text-white transition hover:translate-x-0.5 hover:translate-y-0.5 disabled:opacity-50"
                        style={{ boxShadow: '4px 4px 0 #151827' }}
                    >
                        {busy ? t('revision.starting') : `${t('revision.confirm_action')} ➤`}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

function DashboardButton({ icon, title, desc, disabled, onClick, isCompany = true, accent = 'green', rotate = '-rotate-1' }) {
    if (!isCompany) {
        // Kandidaat: chaotisch landing-stijl, geen icoon
        return (
            <button
                disabled={disabled}
                onClick={onClick}
                className={`touch-target min-h-[88px] rounded-lg border p-4 text-left transition-all ${disabled ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed' : 'bg-[#fffdf8] border-2 border-[#151827]/35 hover:border-[#3f7f65] hover:bg-white hover:-translate-y-0.5'}`}
            >
                <div className="mb-2">
                    <TornBadge variant={accent} rotate={rotate} size="md">{title}</TornBadge>
                </div>
                <div className="text-xs text-[#151827]/60 break-words">{desc}</div>
            </button>
        );
    }
    return (
        <button 
            disabled={disabled}
            onClick={onClick}
            className={`touch-target min-h-[72px] flex items-start rounded-lg border p-3 text-left transition-all sm:p-4 ${disabled ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white border-gray-200 hover:border-brand-green hover:shadow-md'}`}
        >
            <span className="text-xl sm:text-2xl mr-2 sm:mr-3 flex-shrink-0">{icon}</span>
            <div className="min-w-0">
                <div className="font-bold text-sm">{title}</div>
                <div className="text-xs text-gray-500 break-words">{desc}</div>
            </div>
        </button>
    )
}

// --- DE MODAL COMPONENT ---

function ProfileModal({ data, loading, onClose, isCompany, onRegenerateTrialPeriodExamples }) {
    const { t } = useTranslation();

    if (!data && loading) {
        return createPortal(
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm">
                <div className="rounded-[1.2rem] border-4 border-[#151827] bg-white p-8 shadow-[10px_10px_0_#151827]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green mx-auto mb-4"></div>
                    <p className="text-gray-500 font-medium">{t('dashboard_modals.profile.loading')}</p>
                </div>
            </div>,
            document.body
        );
    }

    if (!data) return null;

    // BELANGRIJK: De backend stuurt {"profile": {...}}, dus we moeten data.profile gebruiken
    const profile = data.profile || data; // Fallback voor oude structuur
    
    // Als het een bedrijf is, toon ander profiel
    if (isCompany) {
        return <CompanyProfileModal profile={profile} onClose={onClose} onRegenerateTrialPeriodExamples={onRegenerateTrialPeriodExamples} />;
    }
    
    // Data normalisatie: platte structuur (nieuw) of legacy general nesting
    const personalia = profile.personalia || profile.general?.personalia || {};
    const digitalTwin = profile.digital_twin || profile.general?.digital_twin || {};
    
    // Specifieke candidate velden
    const ambition = profile.ambition || {};
    const esco = profile.esco || {};
    const chosenOccupation = esco.chosen_occupation || profile.chosen_occupation || null;
    const skills = esco.skills || {}; // Dict van skills

    // Richting-flow (2026-07): gekozen richtingen + AI-oordeel welke beter past
    const chosenDirections = esco.chosen_directions || [];
    const directionGroups = esco.direction_groups || [];
    const directionFit = (esco.flags && esco.flags.direction_fit) || null;

    // Bedrijfsfunctie-fit (post-intake AI-analyse): alleen top 3 + groeipaden tonen.
    const businessFit = profile.business_function_fit || null;
    const businessTopFits = (businessFit && businessFit.top_fits) || [];
    const businessGrowthPaths = (businessFit && businessFit.growth_paths) || [];
    
    // BELANGRIJK: Constraints uit de aparte constraints module (niet uit digitalTwin!)
    const constraints = profile.constraints || {};
    const constraintImportance = profile.constraint_importance || {};
    
    // Beide narratives apart ophalen
    const digitalTwinNarrative = digitalTwin.narrative_profile || null;
    const ambitionNarrative = ambition.narrative_profile || null;

    return createPortal(
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-hidden">
            <div className="flex max-h-[90vh] w-full max-w-4xl animate-fade-in-up flex-col overflow-hidden rounded-[1.4rem] border-4 border-[#151827] bg-white shadow-[14px_14px_0_#151827]">
                
                {/* Modal Header */}
                <div className="flex items-center justify-between rounded-t-[1.1rem] border-b-2 border-[#151827]/20 bg-[#fffdf8] p-6">
                    <div>
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                            <ChaosTitle variant="dark" rotate="-rotate-2">{t('dashboard_modals.profile.title_candidate')}</ChaosTitle>
                            <ChaosTitle variant="green" rotate="rotate-2">{t('dashboard_modals.profile.title_profile')}</ChaosTitle>
                        </div>
                        <p className="text-sm text-[#151827]/60">{t('dashboard_modals.profile.subtitle', { name: personalia.name || t('dashboard_modals.profile.unknown') })}</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="h-10 w-10 rounded-full border-2 border-[#151827]/30 bg-white text-xl font-bold text-gray-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-500"
                    >
                        ✕
                    </button>
                </div>

                {/* Modal Content - Scrollable */}
                <div className="flex-1 min-h-0 p-8 overflow-y-auto space-y-8 custom-scrollbar bg-[#f6f1e8]">
                    
                    {/* 1. PERSONALIA & ROL */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="col-span-1 bg-[#fffdf8] p-5 rounded-xl border-2 border-[#151827]/20 shadow-sm">
                            <div className="mb-3"><TornBadge variant="green" rotate="-rotate-2">{t('dashboard_modals.profile.personal_data')}</TornBadge></div>
                            <div className="space-y-2 text-sm text-gray-700">
                                <p><span className="font-semibold text-gray-900">{t('dashboard_modals.profile.name')}:</span> {personalia.name}</p>
                                <p><span className="font-semibold text-gray-900">{t('dashboard_modals.profile.email')}:</span> {personalia.email}</p>
                                <p><span className="font-semibold text-gray-900">{t('dashboard_modals.profile.phone')}:</span> {personalia.phone || '-'}</p>
                                <p><span className="font-semibold text-gray-900">{t('dashboard_modals.profile.region')}:</span> {personalia.location || '-'}</p>
                            </div>
                        </div>

                        <div className="col-span-1 md:col-span-2 bg-[#fffdf8] p-5 rounded-xl border-2 border-[#151827]/20 shadow-sm">
                            <div className="mb-3"><TornBadge variant="purple" rotate="rotate-1">
                                {chosenDirections.length === 2 ? t('dashboard_modals.profile.chosen_directions') : t('dashboard_modals.profile.chosen_occupation')}
                            </TornBadge></div>
                            {chosenDirections.length === 2 ? (
                                <div className="space-y-2">
                                    {chosenDirections.map((uri) => {
                                        const group = directionGroups.find(g => g.direction_uri === uri);
                                        const isBest = directionFit && directionFit.best_direction_uri === uri;
                                        return (
                                            <div key={uri} className="flex items-center gap-2">
                                                <span className={`text-lg font-black ${isBest ? "text-[#3f7f65]" : "text-gray-700"}`}>
                                                    {group ? group.direction_label : uri.split("/").pop()}
                                                </span>
                                                {isBest && (
                                                    <span className="rounded-full bg-[#3f7f65] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                                                        {t('dashboard_modals.profile.best_fit')}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                    <p className="text-xs text-gray-500 italic mt-2">
                                        {t('dashboard_modals.profile.directions_explored')}
                                    </p>
                                </div>
                            ) : chosenOccupation ? (
                                <div>
                                    <div className="text-xl font-black text-[#3f7f65]">{chosenOccupation.label}</div>
                                    <p className="text-sm text-gray-600 mt-1 italic">
                                        {t('dashboard_modals.profile.occupation_selected')}
                                    </p>
                                </div>
                            ) : (
                                <p className="text-gray-400 italic text-sm">{t('dashboard_modals.profile.nothing_chosen')}</p>
                            )}
                        </div>
                    </div>

                    {/* 2. IDENTITEITSPROFIEL */}
                    {digitalTwinNarrative && (
                        <div>
                            <div className="mb-4"><ChaosTitle variant="dark" rotate="-rotate-2">{t('dashboard_modals.profile.identity_sketch')}</ChaosTitle></div>
                            <div className="bg-[#fffdf8] border-2 border-[#151827]/20 p-6 rounded-xl text-sm shadow-sm">
                                <FormattedText text={digitalTwinNarrative} />
                            </div>
                        </div>
                    )}

                    {/* 3. AMBITION PROFILE */}
                    {ambitionNarrative && (
                        <div>
                            <div className="mb-4"><ChaosTitle variant="purple" rotate="rotate-1">{t('dashboard_modals.profile.professional_ambitions')}</ChaosTitle></div>
                            <div className="bg-[#fffdf8] border-2 border-[#151827]/20 p-6 rounded-xl text-sm shadow-sm">
                                <FormattedText text={ambitionNarrative} />
                            </div>
                        </div>
                    )}

                    {/* 3b. RICHTING-FIT (nieuwe flow 2026-07): AI-oordeel welke van de 2 gekozen richtingen beter past */}
                    {directionFit && directionFit.directions && directionFit.directions.length === 2 && (
                        <div>
                            <div className="mb-4"><ChaosTitle variant="green" rotate="rotate-1">{t('dashboard_modals.profile.direction_fit')}</ChaosTitle></div>
                            <div className="bg-[#fffdf8] border-2 border-[#151827]/20 p-6 rounded-xl shadow-sm space-y-5">
                                {directionFit.summary && (
                                    <p className="text-sm text-gray-700 leading-relaxed italic">
                                        {directionFit.summary}
                                    </p>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {directionFit.directions.map((d) => {
                                        const isBest = d.direction_uri === directionFit.best_direction_uri;
                                        const group = directionGroups.find(g => g.direction_uri === d.direction_uri);
                                        const label = group ? group.direction_label : d.direction_uri.split("/").pop();
                                        const fitPct = Math.round((d.fit_score || 0) * 100);
                                        return (
                                            <div
                                                key={d.direction_uri}
                                                className={`
                                                    rounded-xl border-2 p-4
                                                    ${isBest ? "border-[#3f7f65] bg-green-50" : "border-[#151827]/15 bg-white"}
                                                `}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className={`font-bold text-sm ${isBest ? "text-[#3f7f65]" : "text-gray-800"}`}>
                                                        {label}
                                                    </span>
                                                    {isBest && (
                                                        <span className="rounded-full bg-[#3f7f65] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                                                            {t('dashboard_modals.profile.best_fit')}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Fit-bar */}
                                                <div className="mb-3">
                                                    <div className="flex justify-between text-[10px] font-bold text-gray-500 uppercase mb-1">
                                                        <span>{t('dashboard_modals.profile.fit_score')}</span>
                                                        <span>{fitPct}% · {d.confidence || "?"}</span>
                                                    </div>
                                                    <div className="h-2 w-full rounded-full bg-gray-100">
                                                        <div
                                                            className={`h-2 rounded-full ${isBest ? "bg-[#3f7f65]" : "bg-gray-400"}`}
                                                            style={{ width: `${fitPct}%` }}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Evidence for */}
                                                {d.evidence_for && d.evidence_for.length > 0 && (
                                                    <div className="mb-2">
                                                        <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">{t('dashboard_modals.profile.evidence_for')}</div>
                                                        <ul className="space-y-1">
                                                            {d.evidence_for.map((e, i) => (
                                                                <li key={i} className="text-xs text-gray-700 flex gap-1.5">
                                                                    <span className="text-[#3f7f65]">✓</span>
                                                                    <span>{e}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {/* Evidence against */}
                                                {d.evidence_against && d.evidence_against.length > 0 && (
                                                    <div>
                                                        <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">{t('dashboard_modals.profile.evidence_against')}</div>
                                                        <ul className="space-y-1">
                                                            {d.evidence_against.map((e, i) => (
                                                                <li key={i} className="text-xs text-gray-500 flex gap-1.5">
                                                                    <span className="text-amber-500">!</span>
                                                                    <span>{e}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {directionFit.advice && (
                                    <div className="rounded-lg bg-[#f6f1e8] border border-[#151827]/15 p-3">
                                        <div className="text-[10px] font-bold text-[#151827]/70 uppercase mb-1">{t('dashboard_modals.profile.advice')}</div>
                                        <p className="text-sm text-gray-700">{directionFit.advice}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 3c. BEDRIJFSFUNCTIE-FIT: top 3 aansluitingen + groeipaden */}
                    {(businessTopFits.length > 0 || businessGrowthPaths.length > 0) && (
                        <div>
                            <div className="mb-4"><ChaosTitle variant="purple" rotate="-rotate-1">{t('dashboard_modals.profile.business_functions')}</ChaosTitle></div>
                            <div className="bg-[#fffdf8] border-2 border-[#151827]/20 p-6 rounded-xl shadow-sm space-y-6">
                                {businessFit.summary && (
                                    <p className="text-sm text-gray-700 leading-relaxed italic">{businessFit.summary}</p>
                                )}

                                {businessTopFits.length > 0 && (
                                    <div>
                                        <div className="mb-3"><TornBadge variant="green" rotate="-rotate-2">{t('dashboard_modals.profile.best_match')}</TornBadge></div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {businessTopFits.map((f, idx) => {
                                                const scorePct = Math.round((Number(f.fit_score) || 0) * 10);
                                                return (
                                                    <div
                                                        key={f.function_key || idx}
                                                        className={`rounded-xl border-2 p-4 ${idx === 0 ? "border-[#3f7f65] bg-green-50" : "border-[#151827]/15 bg-white"}`}
                                                    >
                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                            <span className={`font-bold text-sm ${idx === 0 ? "text-[#3f7f65]" : "text-gray-800"}`}>
                                                                {f.function_label}
                                                            </span>
                                                            <span className="shrink-0 rounded-full border border-[#151827]/25 bg-[#f6f1e8] px-2 py-0.5 text-[10px] font-bold text-[#151827]/75">
                                                                #{f.rank || idx + 1}
                                                            </span>
                                                        </div>

                                                        <div className="mb-3">
                                                            <div className="flex justify-between text-[10px] font-bold text-gray-500 uppercase mb-1">
                                                                <span>{t('dashboard_modals.profile.fit_score')}</span>
                                                                <span>{f.fit_score}/10</span>
                                                            </div>
                                                            <div className="h-2 w-full rounded-full bg-gray-100">
                                                                <div
                                                                    className={`h-2 rounded-full ${idx === 0 ? "bg-[#3f7f65]" : "bg-gray-400"}`}
                                                                    style={{ width: `${scorePct}%` }}
                                                                />
                                                            </div>
                                                        </div>

                                                        {f.motivation && (
                                                            <p className="text-xs text-gray-700 leading-relaxed">{f.motivation}</p>
                                                        )}

                                                        {f.evidence && f.evidence.length > 0 && (
                                                            <ul className="mt-2 space-y-1">
                                                                {f.evidence.map((e, i) => (
                                                                    <li key={i} className="text-xs text-gray-600 flex gap-1.5">
                                                                        <span className="text-[#3f7f65]">✓</span>
                                                                        <span>{e}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {businessGrowthPaths.length > 0 && (
                                    <div>
                                        <div className="mb-3"><TornBadge variant="purple" rotate="rotate-1">{t('dashboard_modals.profile.growth_paths')}</TornBadge></div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {businessGrowthPaths.map((g, idx) => (
                                                <div key={g.function_key || idx} className="rounded-xl border-2 border-[#151827]/15 bg-white p-4">
                                                    <div className="flex items-start justify-between gap-2 mb-2">
                                                        <span className="font-bold text-sm text-gray-800">{g.function_label}</span>
                                                        {g.horizon && (
                                                            <span className="shrink-0 rounded-full border border-[#151827]/25 bg-[#f6f1e8] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#151827]/75">
                                                                {g.horizon}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {(g.current_fit_score || g.potential_fit_score) && (
                                                        <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">
                                                            {t('dashboard_modals.profile.growth_now_potential', { current: g.current_fit_score, potential: g.potential_fit_score })}
                                                        </p>
                                                    )}

                                                    {g.rationale && (
                                                        <p className="text-xs text-gray-700 leading-relaxed">{g.rationale}</p>
                                                    )}

                                                    {g.required_development && g.required_development.length > 0 && (
                                                        <div className="mt-2">
                                                            <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">{t('dashboard_modals.profile.to_develop')}</div>
                                                            <ul className="space-y-1">
                                                                {g.required_development.map((d, i) => (
                                                                    <li key={i} className="text-xs text-gray-600 flex gap-1.5">
                                                                        <span className="text-[#713d8d]">→</span>
                                                                        <span>{d}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 3. SKILLS */}
                    <div>
                        <div className="mb-1 flex flex-wrap items-baseline gap-2">
                            <ChaosTitle variant="green" rotate="-rotate-1">{t('dashboard_modals.profile.skills')}</ChaosTitle>
                            {Object.keys(skills).length > 0 && (
                                <span className="text-xs italic text-gray-400">{t('dashboard_modals.profile.skills_hint')}</span>
                            )}
                        </div>
                        {Object.keys(skills).length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-3">
                                {Object.values(skills)
                                    .filter(s => s.score !== null && s.score > 0) // Alleen gescoorde skills
                                    .sort((a, b) => b.score - a.score) // Hoogste eerst
                                    .map((skill) => (
                                    <div key={skill.uri} className="py-2 border-b border-[#151827]/10 last:border-0">
                                        <span className="text-sm font-medium text-gray-700" title={skill.label}>
                                            {skill.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-400 italic text-sm">{t('dashboard_modals.profile.no_skills')}</p>
                        )}
                    </div>

                    {/* 4. CONSTRAINTS */}
                    <div className="bg-[#fffdf8] p-5 rounded-xl border-2 border-[#151827]/20">
                        <div className="mb-4"><TornBadge variant="purple" rotate="-rotate-2">{t('dashboard_modals.profile.conditions')}</TornBadge></div>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(constraints).map(([key, val]) => {
                                if (!isConstraintActive(val)) return null;
                                const importanceValue = Object.prototype.hasOwnProperty.call(constraintImportance, key)
                                    ? constraintImportance[key]
                                    : val;
                                return (
                                    <span key={key} className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-[#151827]/20 text-[#151827] rounded-md text-xs font-semibold shadow-sm">
                                        <span>{getConstraintLabel(t, key)}</span>
                                        <span className="rounded-full border border-[#151827]/25 bg-[#f6f1e8] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#151827]/75">
                                            {getConstraintImportance(t, importanceValue)}
                                        </span>
                                    </span>
                                );
                            })}
                            {Object.values(constraints).every(v => !isConstraintActive(v)) && (
                                <span className="text-xs text-orange-400 italic">{t('dashboard_modals.profile.no_conditions')}</span>
                            )}
                        </div>
                    </div>

                </div>

                {/* Modal Footer */}
                <div className="flex justify-end rounded-b-[1.1rem] border-t-2 border-[#151827]/20 bg-[#fffdf8] p-6">
                    <button 
                        onClick={onClose}
                        className="rounded-lg border-2 border-[#151827]/35 bg-white px-6 py-2 font-bold text-gray-700 transition hover:bg-gray-50"
                    >
                        {t('common.close')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

function MonitoringModal({ user, apiBase, onClose, isCompany }) {
    const { t, language } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        identity: "",
        ambition: "",
        skills: "",
        constraints: ""
    });

    // 1. Logs ophalen bij openen
    useEffect(() => {
        async function fetchLogs() {
            try {
                const res = await fetch(`${apiBase}/api/onboarding/progress?userId=${encodeURIComponent(user.id)}`);
                if (res.ok) {
                    const data = await res.json();
                    setLogs(data);
                }
            } catch (e) {
                console.error("Fout bij laden logs:", e);
            } finally {
                setLoading(false);
            }
        }
        fetchLogs();
    }, [apiBase, user.id]);

    // 2. Log opslaan
    const handleSubmit = async () => {
        if (!formData.identity && !formData.ambition && !formData.skills && !formData.constraints) {
            alert(t('dashboard_modals.monitoring.errors.fill_one_field'));
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                user_id: user.id,
                identity_text: formData.identity,
                ambition_text: formData.ambition,
                skills_text: formData.skills,
                constraints_text: formData.constraints
            };

            const res = await fetch(`${apiBase}/api/onboarding/progress`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                // Reset form
                setFormData({ identity: "", ambition: "", skills: "", constraints: "" });
                // Refresh list (simpelweg ophalen of lokaal toevoegen)
                const newLog = {
                    id: Date.now(), // tijdelijk ID
                    created_at: new Date().toISOString(),
                    ...formData
                };
                setLogs([newLog, ...logs]); // Voeg toe bovenaan
            } else {
                alert(t('dashboard_modals.monitoring.errors.save_failed'));
            }
        } catch (e) {
            console.error(e);
            alert(t('common.unknown_error'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleChange = (field, val) => {
        setFormData(prev => ({ ...prev, [field]: val }));
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="flex h-[85vh] w-full max-w-6xl animate-fade-in-up overflow-hidden rounded-[1.4rem] border-4 border-[#151827] bg-white shadow-[14px_14px_0_#151827]">
                
                {/* LINKERKANT: Historie (1/3 breedte) */}
                <div className="flex w-1/3 flex-col border-r border-[#151827]/25 bg-[#f6f1e8]">
                    <div className="border-b border-[#151827]/25 bg-[#fffdf8] p-6">
                        <h2 className="text-xl font-bold text-gray-800">{t('dashboard_modals.monitoring.history_title')}</h2>
                        <p className="text-xs text-gray-500 mt-1">{t('dashboard_modals.monitoring.history_subtitle')}</p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                        {loading ? (
                            <p className="text-center text-gray-400 text-sm mt-10">{t('common.loading')}</p>
                        ) : logs.length === 0 ? (
                            <p className="text-center text-gray-400 text-sm mt-10 italic">{t('dashboard_modals.monitoring.no_logs')}</p>
                        ) : (
                            logs.map((log) => (
                                <div key={log.id} className="rounded-xl border-2 border-[#151827]/20 bg-[#fffdf8] p-4 text-sm shadow-sm">
                                    <div className="text-xs font-bold text-brand-green mb-2">
                                        {new Date(log.created_at).toLocaleString(language === 'en' ? 'en-GB' : 'nl-NL')}
                                    </div>
                                    {log.identity && (
                                        <div className="mb-2">
                                            <span className="font-semibold text-gray-700 text-xs uppercase">{t('dashboard_modals.monitoring.identity_label')}</span>
                                            <p className="text-gray-600 line-clamp-2">{log.identity}</p>
                                        </div>
                                    )}
                                    {log.ambition && (
                                        <div className="mb-2">
                                            <span className="font-semibold text-gray-700 text-xs uppercase">{t('dashboard_modals.monitoring.ambition_label')}</span>
                                            <p className="text-gray-600 line-clamp-2">{log.ambition}</p>
                                        </div>
                                    )}
                                    {log.skills && (
                                        <div className="mb-2">
                                            <span className="font-semibold text-gray-700 text-xs uppercase">{t('dashboard_modals.monitoring.skills_label')}</span>
                                            <p className="text-gray-600 line-clamp-2">{log.skills}</p>
                                        </div>
                                    )}
                                     {log.constraints && (
                                        <div>
                                            <span className="font-semibold text-gray-700 text-xs uppercase">{t('dashboard_modals.monitoring.constraints_label')}</span>
                                            <p className="text-gray-600 line-clamp-2">{log.constraints}</p>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* RECHTERKANT: Invullen (2/3 breedte) */}
                <div className="flex w-2/3 flex-col bg-[#fffdf8]">
                    <div className="flex items-center justify-between border-b border-[#151827]/20 p-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">{t('dashboard_modals.monitoring.new_evaluation_title')}</h2>
                            <p className="text-sm text-gray-500">{t('dashboard_modals.monitoring.new_evaluation_subtitle')}</p>
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">✕</button>
                    </div>

                    <div className="custom-scrollbar flex-1 overflow-y-auto bg-[#f6f1e8]/45 p-8">
                        <div className="grid grid-cols-1 gap-8">
                            {/* Veld 1: Identiteit */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">{t('dashboard_modals.monitoring.identity_title')}</label>
                                <p className="text-xs text-gray-500 mb-2">{t('dashboard_modals.monitoring.identity_hint')}</p>
                                <textarea 
                                    className="h-24 w-full resize-none rounded-lg border-2 border-[#151827]/25 p-3 outline-none transition focus:border-[#3f7f65]"
                                    placeholder={t('dashboard_modals.monitoring.identity_placeholder')}
                                    value={formData.identity}
                                    onChange={(e) => handleChange('identity', e.target.value)}
                                />
                            </div>

                            {/* Veld 2: Ambitie */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">{t('dashboard_modals.monitoring.ambition_title')}</label>
                                <p className="text-xs text-gray-500 mb-2">{t('dashboard_modals.monitoring.ambition_hint')}</p>
                                <textarea 
                                    className="h-24 w-full resize-none rounded-lg border-2 border-[#151827]/25 p-3 outline-none transition focus:border-[#3f7f65]"
                                    placeholder={t('dashboard_modals.monitoring.common_placeholder')}
                                    value={formData.ambition}
                                    onChange={(e) => handleChange('ambition', e.target.value)}
                                />
                            </div>

                            {/* Veld 3: Skills */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">{t('dashboard_modals.monitoring.skills_title')}</label>
                                <p className="text-xs text-gray-500 mb-2">{t('dashboard_modals.monitoring.skills_hint')}</p>
                                <textarea 
                                    className="h-24 w-full resize-none rounded-lg border-2 border-[#151827]/25 p-3 outline-none transition focus:border-[#3f7f65]"
                                    placeholder={t('dashboard_modals.monitoring.common_placeholder')}
                                    value={formData.skills}
                                    onChange={(e) => handleChange('skills', e.target.value)}
                                />
                            </div>

                            {/* Veld 4: Randvoorwaarden */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">{t('dashboard_modals.monitoring.constraints_title')}</label>
                                <p className="text-xs text-gray-500 mb-2">{t('dashboard_modals.monitoring.constraints_hint')}</p>
                                <textarea 
                                    className="h-24 w-full resize-none rounded-lg border-2 border-[#151827]/25 p-3 outline-none transition focus:border-[#3f7f65]"
                                    placeholder={t('dashboard_modals.monitoring.common_placeholder')}
                                    value={formData.constraints}
                                    onChange={(e) => handleChange('constraints', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 border-t border-[#151827]/20 bg-[#fffdf8] p-6">
                        <button onClick={onClose} className="px-6 py-2 rounded-lg font-bold text-gray-600 hover:bg-gray-200 transition">
                            {t('common.cancel')}
                        </button>
                        <button 
                            onClick={handleSubmit}
                            disabled={submitting}
                            className={`rounded-lg bg-[#3f7f65] px-6 py-2 font-bold text-white shadow-md transition hover:opacity-90 ${submitting ? 'opacity-50' : ''}`}
                        >
                            {submitting ? t('dashboard_modals.monitoring.saving') : t('dashboard_modals.monitoring.save_log')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ContractModal({ user, apiBase, onClose, isCompany }) {
    const { t } = useTranslation();
    const [htmlContent, setHtmlContent] = useState("");
    const [status, setStatus] = useState("LOADING"); // LOADING, DRAFT, SIGNED
    const [accepted, setAccepted] = useState(false);
    const [signing, setSigning] = useState(false);

    useEffect(() => {
        async function fetchContract() {
            try {
                const res = await fetch(`${apiBase}/api/contract/view?userId=${user.id}`);
                const data = await res.json();
                setHtmlContent(data.html);
                setStatus(data.status);
            } catch (e) {
                console.error("Error loading contract:", e);
            }
        }
        fetchContract();
    }, [apiBase, user.id]);

    const handleSign = async () => {
        if (!accepted) return alert(t('dashboard_modals.contract.must_accept'));
        setSigning(true);
        try {
            const res = await fetch(`${apiBase}/api/contract/sign`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId: user.id })
            });
            if (res.ok) {
                setStatus("SIGNED");
                // Herlaad de view (optioneel, maar PDF download knop verschijnt nu)
            } else {
                alert(t('dashboard_modals.contract.sign_failed'));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSigning(false);
        }
    };

    const downloadUrl = `${apiBase}/api/contract/download?userId=${user.id}`;

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="h-[90vh] w-full max-w-4xl animate-fade-in-up flex-col rounded-[1.4rem] border-4 border-[#151827] bg-white shadow-[14px_14px_0_#151827]">
                
                {/* HEADER */}
                <div className="flex items-center justify-between rounded-t-[1.1rem] border-b-2 border-[#151827]/20 bg-[#fffdf8] p-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">{t('dashboard_modals.contract.title')}</h2>
                        <p className="text-sm text-gray-500">
                            {status === "SIGNED" ? t('dashboard_modals.contract.status_signed') : t('dashboard_modals.contract.status_draft')}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">✕</button>
                </div>

                {/* CONTENT (Scrollable HTML Preview) */}
                <div className="custom-scrollbar flex-1 overflow-y-auto bg-[#f6f1e8]/45 p-8">
                    <div 
                        className="mx-auto min-h-full max-w-3xl border-2 border-[#151827]/20 bg-[#fffdf8] p-10 shadow-sm"
                        dangerouslySetInnerHTML={{ __html: htmlContent }} 
                    />
                </div>

                {/* FOOTER */}
                <div className="flex items-center justify-between rounded-b-[1.1rem] border-t-2 border-[#151827]/20 bg-[#fffdf8] p-6">
                    
                    {status === "SIGNED" ? (
                        <div className="flex items-center gap-4 w-full justify-between">
                            <span className="text-green-600 font-bold flex items-center gap-2">
                                {t('dashboard_modals.contract.signed_saved')}
                            </span>
                            <a 
                                href={downloadUrl} 
                                target="_blank" 
                                rel="noreferrer"
                                className="rounded-lg bg-[#3f7f65] px-6 py-3 font-bold text-white shadow-md transition hover:opacity-90"
                            >
                                {t('dashboard_modals.contract.download_pdf')}
                            </a>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between w-full">
                            <label className="flex items-center gap-3 cursor-pointer select-none">
                                <input 
                                    type="checkbox" 
                                    className="w-5 h-5 text-brand-green rounded focus:ring-brand-green"
                                    checked={accepted}
                                    onChange={(e) => setAccepted(e.target.checked)}
                                />
                                <span className="text-gray-700 font-medium">{t('dashboard_modals.contract.accept_terms')}</span>
                            </label>
                            
                            <div className="flex gap-3">
                                <button onClick={onClose} className="px-6 py-2 text-gray-500 hover:bg-gray-100 rounded-lg font-bold">
                                    {t('common.cancel')}
                                </button>
                                <button 
                                    onClick={handleSign}
                                    disabled={!accepted || signing}
                                    className={`px-8 py-3 rounded-lg font-bold text-white shadow-md transition ${!accepted ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 hover:scale-105'}`}
                                >
                                    {signing ? t('dashboard_modals.contract.signing') : t('dashboard_modals.contract.sign_cta')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// --- COMPANY PROFILE MODAL ---

function CompanyProfileModal({ profile, onClose, onRegenerateTrialPeriodExamples }) {
    const { t } = useTranslation();
    const [regeneratingTrials, setRegeneratingTrials] = useState(false);

    // Data normalisatie voor bedrijven
    const personalia = profile.personalia || profile.general?.personalia || {};
    const digitalTwin = profile.digital_twin || profile.general?.digital_twin || {};
    const ambition = profile.ambition || {};
    const constraints = profile.constraints || {};
    const vacancies = profile.vacancy_revised?.vacancies || profile.vacancy_search?.vacancies || [];
    const trialPeriodExamples = profile.trial_period_examples || [];
    
    const digitalTwinNarrative = digitalTwin.narrative_profile || null;
    const ambitionNarrative = ambition.narrative_profile || null;

    return createPortal(
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-hidden">
            <div className="flex max-h-[90vh] w-full max-w-4xl animate-fade-in-up flex-col overflow-hidden rounded-[1.4rem] border-4 border-[#151827] bg-white shadow-[14px_14px_0_#151827]">
                
                {/* Modal Header */}
                <div className="flex items-center justify-between rounded-t-[1.1rem] border-b-2 border-[#151827]/20 bg-[#fffdf8] p-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">{t('dashboard_modals.company_profile.title')}</h2>
                        <p className="text-sm text-gray-500">{t('dashboard_modals.company_profile.subtitle', { name: personalia.company_name || personalia.name || t('dashboard_modals.company_profile.unknown') })}</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="h-10 w-10 rounded-full border-2 border-[#151827]/30 bg-white text-xl font-bold text-gray-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-500"
                    >
                        ✕
                    </button>
                </div>

                {/* Modal Content - Scrollable */}
                <div className="flex-1 min-h-0 p-8 overflow-y-auto space-y-8 custom-scrollbar">
                    
                    {/* 1. BEDRIJFSGEGEVENS */}
                    <div className="bg-purple-50 p-6 rounded-xl border border-purple-100">
                        <h3 className="text-xs font-bold text-brand-purple uppercase tracking-wider mb-4">{t('dashboard_modals.company_profile.company_data')}</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                            <div>
                                <span className="font-semibold text-gray-900">{t('dashboard_modals.company_profile.company_name')}:</span>
                                <p className="mt-1">{personalia.company_name || '-'}</p>
                            </div>
                            <div>
                                <span className="font-semibold text-gray-900">{t('dashboard_modals.company_profile.sector')}:</span>
                                <p className="mt-1">{personalia.sector || '-'}</p>
                            </div>
                            <div>
                                <span className="font-semibold text-gray-900">{t('dashboard_modals.company_profile.location')}:</span>
                                <p className="mt-1">{personalia.location || '-'}</p>
                            </div>
                            <div>
                                <span className="font-semibold text-gray-900">{t('dashboard_modals.company_profile.contact_person')}:</span>
                                <p className="mt-1">{personalia.name || '-'}</p>
                            </div>
                            <div>
                                <span className="font-semibold text-gray-900">{t('dashboard_modals.company_profile.function')}:</span>
                                <p className="mt-1">{personalia.function || '-'}</p>
                            </div>
                            {personalia.one_liner_description && (
                                <div className="col-span-2">
                                    <span className="font-semibold text-gray-900">{t('dashboard_modals.company_profile.description')}:</span>
                                    <p className="mt-1 italic">{personalia.one_liner_description}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 2. IDENTITEITSPROFIEL */}
                    {digitalTwinNarrative && (
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span>🏢</span> {t('dashboard_modals.company_profile.company_identity')}
                            </h3>
                            <div className="bg-purple-50 border border-purple-200 p-6 rounded-xl text-sm shadow-sm">
                                <FormattedText text={digitalTwinNarrative} />
                            </div>
                        </div>
                    )}

                    {/* 3. AMBITION PROFILE */}
                    {ambitionNarrative && (
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span>🎯</span> {t('dashboard_modals.company_profile.organization_ambitions')}
                            </h3>
                            <div className="bg-purple-50 border border-purple-200 p-6 rounded-xl text-sm shadow-sm">
                                <FormattedText text={ambitionNarrative} />
                            </div>
                        </div>
                    )}

                    {/* 4. VACATURES */}
                    {vacancies && vacancies.length > 0 && (
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <span>💼</span> {t('dashboard_modals.company_profile.vacancies')}
                            </h3>
                            <div className="space-y-4">
                                {vacancies.map((vacancy, idx) => (
                                    <div key={idx} className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <h4 className="font-bold text-lg text-brand-purple">{vacancy.job_title}</h4>
                                                <p className="text-sm text-gray-600 mt-1">
                                                    📍 {vacancy.location} • 🕐 {vacancy.employment_type}
                                                </p>
                                            </div>
                                        </div>
                                        <p className="text-gray-700 text-sm mb-3">{vacancy.job_description}</p>
                                        {vacancy.required_skills && vacancy.required_skills.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-3">
                                                {vacancy.required_skills.map((skill, skillIdx) => (
                                                    <span key={skillIdx} className="px-3 py-1 bg-purple-50 text-brand-purple border border-purple-200 rounded-md text-xs font-semibold">
                                                        {skill}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 5. CONSTRAINTS & RANDVOORWAARDEN */}
                    <div className="bg-orange-50 p-5 rounded-xl border border-orange-100">
                        <h3 className="text-xs font-bold text-orange-800 uppercase tracking-wider mb-4">{t('dashboard_modals.company_profile.conditions_title')}</h3>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(constraints).map(([key, val]) => {
                                if (!isConstraintActive(val)) return null;
                                return (
                                    <span key={key} className="px-3 py-1 bg-white border border-orange-200 text-orange-700 rounded-md text-xs font-semibold shadow-sm">
                                        {getConstraintLabel(t, key)}
                                    </span>
                                );
                            })}
                            {Object.values(constraints).every(v => !isConstraintActive(v)) && (
                                <span className="text-xs text-orange-400 italic">{t('dashboard_modals.company_profile.no_conditions')}</span>
                            )}
                        </div>
                    </div>

                    {/* 6. 100-UUR PROEFPERIODE VOORBEELDEN */}
                    <div>
                        <div className="mb-3 flex items-center justify-between gap-3">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <span>⏱️</span> {t('dashboard_modals.company_profile.trial_title')}
                            </h3>
                            <button
                                type="button"
                                onClick={async () => {
                                    if (!onRegenerateTrialPeriodExamples || regeneratingTrials) return;
                                    setRegeneratingTrials(true);
                                    try {
                                        await onRegenerateTrialPeriodExamples();
                                    } finally {
                                        setRegeneratingTrials(false);
                                    }
                                }}
                                disabled={regeneratingTrials || !onRegenerateTrialPeriodExamples}
                                className="rounded-lg border-2 border-[#151827]/30 bg-white px-3 py-1.5 text-xs font-bold text-[#151827] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {regeneratingTrials ? t('common.loading') : t('dashboard_modals.company_profile.regenerate_trial_period_examples')}
                            </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            {t('dashboard_modals.company_profile.trial_subtitle')}
                        </p>
                        {trialPeriodExamples.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {trialPeriodExamples.map((example, idx) => (
                                    <div key={idx} className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 p-5 rounded-xl shadow-sm">
                                        <h4 className="font-bold text-brand-green text-base mb-2">
                                            {example.title}
                                        </h4>
                                        <p className="text-sm text-gray-700 mb-3">
                                            {example.goal}
                                        </p>
                                        {example.deliverables && example.deliverables.length > 0 && (
                                            <div className="mb-3">
                                                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{t('dashboard_modals.company_profile.deliverables')}:</span>
                                                <ul className="mt-1 space-y-1">
                                                    {example.deliverables.map((deliverable, dIdx) => (
                                                        <li key={dIdx} className="text-sm text-gray-700 flex items-start gap-2">
                                                            <span className="text-green-500 mt-1">✓</span>
                                                            {deliverable}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        <div className="pt-3 border-t border-green-200">
                                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{t('dashboard_modals.company_profile.suitable_for')}:</span>
                                            <p className="text-sm text-gray-700 mt-1 italic">{example.profile}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="rounded-xl border border-dashed border-green-200 bg-green-50/60 p-5 text-sm text-gray-600">
                                Nog geen suggesties gegenereerd. Gebruik de knop hierboven om ze opnieuw op te halen.
                            </div>
                        )}
                    </div>

                </div>

                {/* Modal Footer */}
                <div className="flex justify-end rounded-b-[1.1rem] border-t-2 border-[#151827]/20 bg-[#fffdf8] p-6">
                    <button 
                        onClick={onClose}
                        className="rounded-lg border-2 border-[#151827]/35 bg-white px-6 py-2 font-bold text-gray-700 transition hover:bg-gray-50"
                    >
                        {t('common.close')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
// --- MATCHES MODAL ---

function MatchesModal({ user, apiBase, isCompany, onClose }) {
    const { t, language } = useTranslation();
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [feedbackBusyId, setFeedbackBusyId] = useState(null);
    const [feedbackError, setFeedbackError] = useState(null);

    const sortMatches = (items) => {
        const arr = [...(items || [])];
        arr.sort((a, b) => {
            const aRejected = a.feedback === 'rejected' ? 1 : 0;
            const bRejected = b.feedback === 'rejected' ? 1 : 0;
            if (aRejected !== bRejected) return aRejected - bRejected;
            return (b.score || 0) - (a.score || 0);
        });
        return arr;
    };

    // Haal matches op bij openen
    useEffect(() => {
        async function fetchMatches() {
            try {
                const paramName = isCompany ? 'companyId' : 'candidateId';
                const res = await fetch(`${apiBase}/api/matches?${paramName}=${encodeURIComponent(user.id)}`);
                if (res.ok) {
                    const data = await res.json();
                    const nextMatches = sortMatches(data.matches || []);
                    setMatches(nextMatches);
                    setSelectedMatch((prev) => {
                        if (!nextMatches.length) return null;
                        if (!prev) return nextMatches[0];
                        return nextMatches.find((m) => m.id === prev.id) || nextMatches[0];
                    });
                }
            } catch (e) {
                console.error("Error loading matches:", e);
            } finally {
                setLoading(false);
            }
        }
        fetchMatches();
    }, [apiBase, user.id, isCompany, language]);

    async function submitFeedback(match, feedback) {
        setFeedbackBusyId(match.id);
        setFeedbackError(null);
        const nextFeedback = match.feedback === feedback ? null : feedback;
        try {
            const res = await fetch(`${apiBase}/api/matches/feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ candidateId: user.id, matchId: match.id, feedback: nextFeedback }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            setMatches((prev) => {
                const updated = prev.map((m) => (m.id === match.id ? { ...m, feedback: nextFeedback } : m));
                return sortMatches(updated);
            });
            setSelectedMatch((cur) => (cur && cur.id === match.id ? { ...cur, feedback: nextFeedback } : cur));
        } catch (e) {
            console.error("Error submitting match feedback:", e);
            setFeedbackError(t('dashboard_modals.matches.feedback_failed'));
        } finally {
            setFeedbackBusyId(null);
        }
    }

    const getCategoryLabel = (category) => {
        const labels = {
            digital_twin: t('dashboard_modals.matches.categories.identity'),
            ambition: t('dashboard_modals.matches.categories.ambition'),
            skills: t('dashboard_modals.matches.categories.skills'),
            constraints: t('dashboard_modals.matches.categories.conditions')
        };
        return labels[category] || category;
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="flex h-[85vh] w-full max-w-6xl animate-fade-in-up flex-col overflow-hidden rounded-[1.4rem] border-4 border-[#151827] bg-white shadow-[14px_14px_0_#151827] md:flex-row">
                
                {/* LINKERKANT: Matches Lijst (1/3 breedte) */}
                <div className="flex h-[42%] w-full flex-col border-b border-[#151827]/25 bg-[#f6f1e8] md:h-full md:w-1/3 md:border-b-0 md:border-r">
                    <div className="border-b border-[#151827]/25 bg-[#fffdf8] p-6">
                        <div className="flex flex-wrap items-center gap-2">
                            <ChaosTitle variant="green" rotate="-rotate-2">{isCompany ? t('dashboard_modals.matches.matched_candidates') : t('dashboard.opportunities')}</ChaosTitle>
                            <TornBadge variant="purple" rotate="rotate-2" size="sm">{matches.length}</TornBadge>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">{t('dashboard_modals.matches.click_for_details')}</p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                        {loading ? (
                            <p className="text-center text-gray-400 text-sm mt-10">{t('common.loading')}</p>
                        ) : matches.length === 0 ? (
                            <p className="text-center text-gray-400 text-sm mt-10 italic">{t('dashboard_modals.matches.none_found')}</p>
                        ) : (
                            matches.map((match) => (
                                <button
                                    key={match.id}
                                    onClick={() => setSelectedMatch(match)}
                                    className={`w-full rounded-xl border-2 p-4 text-left transition-all ${
                                        match.feedback === 'rejected'
                                            ? 'border-[#151827]/15 bg-[#eceef2] text-gray-600 hover:border-[#151827]/25'
                                            : 'border-[#151827]/25 bg-[#fffdf8] hover:border-[#3f7f65]'
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className={`font-bold text-sm truncate ${match.feedback === 'rejected' ? 'text-gray-600' : 'text-gray-900'}`}>
                                            {isCompany ? (match.match_with || match.match_reference || '-') : (match.match_reference || match.match_with)}
                                        </h3>
                                        <span className={`text-lg font-bold ${match.feedback === 'rejected' ? 'text-gray-500' : 'text-[#3f7f65]'}`}>
                                            {match.score?.toFixed(1)}
                                        </span>
                                    </div>
                                    <div className={`flex items-center gap-2 text-xs ${match.feedback === 'rejected' ? 'text-gray-500' : 'text-gray-500'}`}>
                                        <span>{new Date(match.created_at).toLocaleDateString(language === 'en' ? 'en-GB' : 'nl-NL')}</span>
                                        {match.feedback === 'liked' && (
                                            <span className="font-semibold text-[#3f7f65]">👍 {t('dashboard_modals.matches.feedback_liked')}</span>
                                        )}
                                        {match.feedback === 'rejected' && (
                                            <span className="font-semibold text-gray-500">👎 {t('dashboard_modals.matches.feedback_reject')}</span>
                                        )}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* RECHTERKANT: Match Details (2/3 breedte) */}
                <div className="flex h-[58%] w-full flex-col bg-[#fffdf8] md:h-full md:w-2/3">
                    <div className="flex items-center justify-between border-b border-[#151827]/20 bg-[#fffdf8] p-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">
                                {selectedMatch ? (isCompany ? (selectedMatch.match_with || selectedMatch.match_reference || '-') : (selectedMatch.match_reference || selectedMatch.match_with)) : t('dashboard_modals.matches.select_match')}
                            </h2>
                            {selectedMatch && (
                                <p className="text-sm text-gray-500 mt-1">
                                    {t('dashboard_modals.matches.reference_label')} <span className="font-bold text-gray-700">{selectedMatch.match_reference || '-'}</span> · {t('dashboard_modals.matches.total_score_label')} <span className="font-bold text-[#3f7f65]">{selectedMatch.score?.toFixed(1)}/10</span>
                                </p>
                            )}
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">✕</button>
                    </div>

                    {/* Kandidaat-feedback: duimpje omhoog of omlaag */}
                    {!isCompany && selectedMatch && (
                        <div className="flex flex-wrap items-center gap-3 border-b border-[#151827]/20 bg-[#f6f1e8] px-6 py-3">
                            <span className="text-sm font-semibold text-gray-700">{t('dashboard_modals.matches.feedback_question')}</span>
                            <button
                                onClick={() => submitFeedback(selectedMatch, 'liked')}
                                disabled={feedbackBusyId === selectedMatch.id}
                                title={t('dashboard_modals.matches.feedback_like')}
                                className={`min-h-[44px] rounded-xl border-2 px-4 py-2 text-sm font-bold transition-all disabled:opacity-60 ${
                                    selectedMatch.feedback === 'liked'
                                        ? 'border-[#3f7f65] bg-[#3f7f65] text-white'
                                        : 'border-[#151827]/25 bg-white text-gray-700 hover:border-[#3f7f65] hover:text-[#3f7f65]'
                                }`}
                            >
                                👍 {t('dashboard_modals.matches.feedback_like')}
                            </button>
                            <button
                                onClick={() => submitFeedback(selectedMatch, 'rejected')}
                                disabled={feedbackBusyId === selectedMatch.id}
                                title={t('dashboard_modals.matches.feedback_reject')}
                                className={`min-h-[44px] rounded-xl border-2 px-4 py-2 text-sm font-bold transition-all disabled:opacity-60 ${
                                    selectedMatch.feedback === 'rejected'
                                        ? 'border-[#8a3c2f] bg-[#8a3c2f] text-white'
                                        : 'border-[#151827]/25 bg-white text-gray-700 hover:border-[#8a3c2f] hover:text-[#8a3c2f]'
                                }`}
                            >
                                👎 {t('dashboard_modals.matches.feedback_reject')}
                            </button>
                            {selectedMatch.feedback === 'liked' && (
                                <span className="text-xs font-semibold text-[#3f7f65]">{t('dashboard_modals.matches.feedback_liked_confirmation')}</span>
                            )}
                            {selectedMatch.feedback === 'rejected' && (
                                <span className="text-xs font-semibold text-[#8a3c2f]">{t('dashboard_modals.matches.feedback_rejected_confirmation')}</span>
                            )}
                            {feedbackError && <span className="text-xs font-semibold text-red-600">{feedbackError}</span>}
                        </div>
                    )}

                    <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                        {!selectedMatch ? (
                            <div className="h-full flex items-center justify-center text-gray-400">
                                <div className="text-center">
                                    <div className="text-6xl mb-4"></div>
                                    <p className="text-lg">{t('dashboard_modals.matches.click_for_details')}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Overall Score Visualization */}
                                <div className={`p-6 rounded-xl border ${
                                    selectedMatch.score >= 8 
                                        ? 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-300' 
                                        : selectedMatch.score >= 6 
                                            ? 'bg-gradient-to-r from-green-50 to-teal-50 border-green-200' 
                                            : 'bg-gradient-to-r from-gray-50 to-slate-100 border-gray-200'
                                }`}>
                                    <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">{t('dashboard_modals.matches.overall_match')}</h3>
                                    <div className="flex items-center gap-4">
                                        <div className={`text-5xl font-bold ${
                                            selectedMatch.score >= 8 
                                                ? 'text-green-600' 
                                                : selectedMatch.score >= 6 
                                                    ? 'text-brand-green' 
                                                    : 'text-gray-600'
                                        }`}>
                                            {selectedMatch.score?.toFixed(1)}
                                        </div>
                                        <div className="flex-1">
                                            <div className="w-full bg-gray-200 rounded-full h-3">
                                                <div 
                                                    className={`h-3 rounded-full ${
                                                        selectedMatch.score >= 8 
                                                            ? 'bg-green-500' 
                                                            : selectedMatch.score >= 6 
                                                                ? 'bg-brand-green' 
                                                                : 'bg-gray-400'
                                                    }`}
                                                    style={{ width: `${selectedMatch.score * 10}%` }}
                                                ></div>
                                            </div>
                                            <p className={`text-sm font-medium mt-2 ${
                                                selectedMatch.score >= 8 
                                                    ? 'text-green-700' 
                                                    : selectedMatch.score >= 6 
                                                        ? 'text-brand-green' 
                                                        : 'text-gray-600'
                                            }`}>
                                                {selectedMatch.score >= 8 ? t('dashboard_modals.matches.excellent_match') : 
                                                 selectedMatch.score >= 6 ? t('dashboard_modals.matches.good_match') : 
                                                 t('dashboard_modals.matches.match')}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Match Breakdown per Category */}
                                <div>
                                    <div className="mb-4">
                                        <ChaosTitle variant="dark" rotate="-rotate-1">{t('dashboard_modals.matches.details_per_category')}</ChaosTitle>
                                    </div>
                                    <div className="space-y-4">
                                        {selectedMatch.levels && selectedMatch.levels.length > 0 ? (
                                            selectedMatch.levels.map((level, idx) => {
                                                const variants = ['green', 'purple', 'dark', 'cream'];
                                                const rotations = ['-rotate-2', 'rotate-1', '-rotate-1', 'rotate-2'];
                                                const v = variants[idx % variants.length];
                                                const r = rotations[idx % rotations.length];
                                                return (
                                                <div key={idx} className="rounded-xl border-2 border-[#151827]/20 bg-[#fffdf8] p-5 shadow-sm transition hover:border-[#3f7f65]">
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div>
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <TornBadge variant={v} rotate={r} size="md">{getCategoryLabel(level.category)}</TornBadge>
                                                                {level.ai_failed && (
                                                                    <span className="rounded bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-700">{t('dashboard_modals.matches.ai_failed')}</span>
                                                                )}
                                                            </div>
                                                            <div className="mt-2 flex items-center gap-2">
                                                                <div className="h-2 w-32 rounded-full bg-gray-100">
                                                                    <div 
                                                                        className="h-2 rounded-full bg-gradient-to-r from-[#3f7f65] to-[#713d8d]"
                                                                        style={{ width: `${Math.max(0, Math.min(100, (level.final_score || 0) * 10))}%` }}
                                                                    ></div>
                                                                </div>
                                                                <span className="text-sm font-bold text-gray-700">
                                                                    {level.final_score?.toFixed(1)}/10
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Speciale weergave voor Randvoorwaarden met matches/mismatches */}
                                                    {level.category === "constraints" && (level.constraint_matches?.length > 0 || level.constraint_mismatches?.length > 0 || level.constraint_partial_mismatches?.length > 0) ? (
                                                        <div className="mt-3 space-y-3">
                                                            {/* Matches - wat beide partijen hebben */}
                                                            {level.constraint_matches && level.constraint_matches.length > 0 && (
                                                                <div className="rounded-lg border border-[#cfe6db] bg-[#eef6f2] p-3">
                                                                    <p className="mb-2 text-xs font-bold uppercase text-[#2b5b49]">{t('dashboard_modals.matches.constraints_match')}</p>
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {level.constraint_matches.map((desc, i) => (
                                                                            <span key={i} className="rounded border border-[#b7d8c7] bg-[#dff0e8] px-2 py-1 text-xs font-medium text-[#214a3a]">
                                                                                {desc}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {/* Partial Mismatches - enigszins belangrijk maar niet aangeboden */}
                                                            {level.constraint_partial_mismatches && level.constraint_partial_mismatches.length > 0 && (
                                                                <div className="rounded-lg border border-[#e7dbc3] bg-[#f8f2e6] p-3">
                                                                    <p className="mb-2 text-xs font-bold uppercase text-[#7a5f35]">{t('dashboard_modals.matches.constraints_partial_mismatch')}</p>
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {level.constraint_partial_mismatches.map((desc, i) => (
                                                                            <span key={i} className="rounded border border-[#ddcdb0] bg-[#f2e7d3] px-2 py-1 text-xs font-medium text-[#6b522d]">
                                                                                {desc}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {/* Full Mismatches - erg belangrijk maar niet aangeboden */}
                                                            {level.constraint_mismatches && level.constraint_mismatches.length > 0 && (
                                                                <div className="rounded-lg border border-[#e7cbc5] bg-[#f9eeeb] p-3">
                                                                    <p className="mb-2 text-xs font-bold uppercase text-[#8a3c2f]">{t('dashboard_modals.matches.constraints_full_mismatch')}</p>
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {level.constraint_mismatches.map((desc, i) => (
                                                                            <span key={i} className="rounded border border-[#dab6ad] bg-[#f2dbd5] px-2 py-1 text-xs font-medium text-[#773126]">
                                                                                {desc}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : level.category === "skills" && (level.skill_exact_matches?.length > 0 || level.skill_hierarchical_matches?.length > 0 || level.skill_semantic_matches?.length > 0 || level.skill_potential_matches?.length > 0 || level.skill_missing?.length > 0) ? (
                                                        <div className="mt-3 space-y-3">
                                                            {/* Exacte matches */}
                                                            <div className={`p-3 rounded-lg border ${level.skill_exact_matches?.length > 0 ? 'bg-[#eef6f2] border-[#cfe6db]' : 'bg-gray-50 border-gray-200'}`}>
                                                                <p className={`text-xs font-bold uppercase mb-2 ${level.skill_exact_matches?.length > 0 ? 'text-[#2b5b49]' : 'text-gray-400'}`}>
                                                                    {t('dashboard_modals.matches.exact_matches')} ({level.skill_exact_matches?.length || 0})
                                                                </p>
                                                                {level.skill_exact_matches?.length > 0 ? (
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {level.skill_exact_matches.map((skill, i) => (
                                                                            <span key={i} className="rounded border border-[#b7d8c7] bg-[#dff0e8] px-2 py-1 text-xs font-medium text-[#214a3a]">
                                                                                {skill}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-xs text-gray-400 italic">{t('dashboard_modals.matches.no_exact_matches')}</p>
                                                                )}
                                                            </div>
                                                            {/* Hiërarchische matches */}
                                                            <div className={`p-3 rounded-lg border ${level.skill_hierarchical_matches?.length > 0 ? 'bg-[#edf3fa] border-[#cfdeef]' : 'bg-gray-50 border-gray-200'}`}>
                                                                <p className={`text-xs font-bold uppercase mb-2 ${level.skill_hierarchical_matches?.length > 0 ? 'text-[#315b84]' : 'text-gray-400'}`}>
                                                                    {t('dashboard_modals.matches.related_skills')} ({level.skill_hierarchical_matches?.length || 0}), {t('dashboard_modals.matches.credit_67')}
                                                                </p>
                                                                {level.skill_hierarchical_matches?.length > 0 ? (
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {level.skill_hierarchical_matches.map((skill, i) => {
                                                                            const parts = skill.split(/[≈~]/);
                                                                            const display = parts.length === 2 
                                                                                ? `${parts[0]} ↔ ${parts[1]}`
                                                                                : skill;
                                                                            return (
                                                                                <span key={i} className="rounded border border-[#b7cce4] bg-[#dfe9f5] px-2 py-1 text-xs font-medium text-[#284b71]">
                                                                                    {display}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-xs text-gray-400 italic">{t('dashboard_modals.matches.no_hierarchical_matches')}</p>
                                                                )}
                                                            </div>
                                                            {/* Semantische matches */}
                                                            <div className={`p-3 rounded-lg border ${level.skill_semantic_matches?.length > 0 ? 'bg-[#f4ecfa] border-[#dcc8ea]' : 'bg-gray-50 border-gray-200'}`}>
                                                                <p className={`text-xs font-bold uppercase mb-2 ${level.skill_semantic_matches?.length > 0 ? 'text-[#6a3d86]' : 'text-gray-400'}`}>
                                                                    {t('dashboard_modals.matches.semantic_related')} ({level.skill_semantic_matches?.length || 0}), {t('dashboard_modals.matches.variable_credit')}
                                                                </p>
                                                                {level.skill_semantic_matches?.length > 0 ? (
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {level.skill_semantic_matches.map((skill, i) => {
                                                                            const parts = skill.split('~');
                                                                            const display = parts.length === 2 
                                                                                ? `${parts[0]} ↔ ${parts[1]}`
                                                                                : skill;
                                                                            return (
                                                                                <span key={i} className="rounded border border-[#ccb2df] bg-[#e8d8f4] px-2 py-1 text-xs font-medium text-[#5a3176]">
                                                                                    {display}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-xs text-gray-400 italic">{t('dashboard_modals.matches.no_semantic_matches')}</p>
                                                                )}
                                                            </div>
                                                            {/* Leerpotentieel matches (Level 4) */}
                                                            <div className={`p-3 rounded-lg border ${level.skill_potential_matches?.length > 0 ? 'bg-[#e9f5f3] border-[#c8e5e0]' : 'bg-gray-50 border-gray-200'}`}>
                                                                <p className={`text-xs font-bold uppercase mb-2 ${level.skill_potential_matches?.length > 0 ? 'text-[#2f6f67]' : 'text-gray-400'}`}>
                                                                    {t('dashboard_modals.matches.learning_potential')} ({level.skill_potential_matches?.length || 0}), {t('dashboard_modals.matches.ai_assessed')}
                                                                </p>
                                                                {level.skill_potential_matches?.length > 0 ? (
                                                                    <>
                                                                        <div className="flex flex-wrap gap-1.5">
                                                                            {level.skill_potential_matches.map((skill, i) => (
                                                                                <span key={i} className="rounded border border-[#b8d8d3] bg-[#d9ede9] px-2 py-1 text-xs font-medium text-[#275b55]">
                                                                                    {skill}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                        {level.skill_potential_explanation && (
                                                                            <p className="mt-2 text-xs italic text-[#2f6f67]">
                                                                                {level.skill_potential_explanation}
                                                                            </p>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <p className="text-xs text-gray-400 italic">{t('dashboard_modals.matches.no_learning_potential')}</p>
                                                                )}
                                                            </div>
                                                            {/* Ontbrekende skills */}
                                                            {level.skill_missing && level.skill_missing.length > 0 && (
                                                                <div className="rounded-lg border border-[#e7cbc5] bg-[#f9eeeb] p-3">
                                                                    <p className="mb-2 text-xs font-bold uppercase text-[#8a3c2f]">{t('dashboard_modals.matches.missing')} ({level.skill_missing.length})</p>
                                                                    <div className="flex flex-wrap gap-1.5">
                                                                        {level.skill_missing.map((skill, i) => (
                                                                            <span key={i} className="rounded border border-[#dab6ad] bg-[#f2dbd5] px-2 py-1 text-xs font-medium text-[#773126]">
                                                                                {skill}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : level.explanation && (
                                                        <div className="mt-3">
                                                            <div className="text-sm bg-gray-50 p-3 rounded-lg border border-gray-100">
                                                                <FormattedText text={level.explanation} />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })
                                        ) : (
                                            <p className="text-gray-400 italic text-sm">{t('dashboard_modals.matches.no_details')}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- WACHTWOORD WIJZIGEN MODAL ---

function ChangePasswordModal({ user, apiBase, isCompany, onClose }) {
    const { t } = useTranslation();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validatie
        if (newPassword.length < 8) {
            setError(t('dashboard_modals.change_password.errors.min_length'));
            return;
        }
        if (newPassword !== confirmPassword) {
            setError(t('dashboard_modals.change_password.errors.mismatch'));
            return;
        }

        if (currentPassword === newPassword) {
            setError(t('dashboard_modals.change_password.errors.same_as_current'));
            return;
        }

        setLoading(true);

        try {
            // Bepaal het juiste endpoint op basis van user type
            const endpoint = isCompany 
                ? `${apiBase}/api/company/auth/change-password`
                : `${apiBase}/api/candidate/auth/change-password`;

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: user.id,
                    current_password: currentPassword,
                    new_password: newPassword
                })
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.detail || t('common.unknown_error'));
            }

            setSuccess(true);
            // Sluit modal na 2 seconden
            setTimeout(() => {
                onClose();
            }, 2000);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="w-full max-w-md animate-fade-in-up rounded-[1.3rem] border-4 border-[#151827] bg-white shadow-[12px_12px_0_#151827]">
                
                {/* Header */}
                <div className="flex items-center justify-between rounded-t-[1rem] border-b-2 border-[#151827]/20 bg-[#fffdf8] p-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">{t('dashboard.change_password')}</h2>
                        <p className="text-sm text-gray-500">{t('dashboard_modals.change_password.subtitle')}</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:bg-red-50 hover:border-red-200 hover:text-red-500 transition-colors text-xl font-bold text-gray-400"
                    >
                        ✕
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {success ? (
                        <div className="text-center py-8">
                            <div className="text-6xl mb-4">✅</div>
                            <h3 className="text-xl font-bold text-green-600 mb-2">{t('dashboard_modals.change_password.success_title')}</h3>
                            <p className="text-gray-500">{t('dashboard_modals.change_password.success_body')}</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('dashboard_modals.change_password.current_password')}
                                </label>
                                <input
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    required
                                    className="w-full rounded-lg border-2 border-[#151827]/25 px-4 py-3 transition focus:border-[#3f7f65] focus:outline-none"
                                    placeholder="••••••••"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('dashboard_modals.change_password.new_password')}
                                </label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    className="w-full rounded-lg border-2 border-[#151827]/25 px-4 py-3 transition focus:border-[#3f7f65] focus:outline-none"
                                    placeholder={t('dashboard_modals.change_password.min_8_placeholder')}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('dashboard_modals.change_password.confirm_new_password')}
                                </label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    className="w-full rounded-lg border-2 border-[#151827]/25 px-4 py-3 transition focus:border-[#3f7f65] focus:outline-none"
                                    placeholder={t('dashboard_modals.change_password.repeat_placeholder')}
                                />
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-200 transition"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 rounded-lg bg-[#3f7f65] px-6 py-3 font-bold text-white transition hover:opacity-90 disabled:opacity-50"
                                >
                                    {loading ? t('dashboard_modals.change_password.loading') : t('dashboard_modals.change_password.submit')}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}


// --- AVG ACCOUNT SETTINGS MODAL ---

function AccountSettingsModal({ user, apiBase, isCompany, onClose, onLogout }) {
    const { t, language } = useTranslation();
    const deleteKeyword = language === 'en' ? 'DELETE' : 'VERWIJDER';
    const [activeTab, setActiveTab] = useState('privacy'); // 'privacy' | 'notifications' | 'export' | 'delete'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [exportData, setExportData] = useState(null);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteSuccess, setDeleteSuccess] = useState(false);
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [notifLoading, setNotifLoading] = useState(true);
    const [notifSaved, setNotifSaved] = useState(false);
    const [languageSaved, setLanguageSaved] = useState(false);

    const persistLanguagePreference = async (nextLang) => {
        const endpoint = isCompany
            ? `${apiBase}/api/company/settings/language?companyId=${encodeURIComponent(user.id)}`
            : `${apiBase}/api/candidate/settings/language?candidateId=${encodeURIComponent(user.id)}`;

        setLanguageSaved(false);
        const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ language: nextLang })
        });
        const data = await res.json();
        if (!res.ok) {
            throw new Error(data.detail || t('common.unknown_error'));
        }
        setLanguageSaved(true);
        setTimeout(() => setLanguageSaved(false), 3000);
        return data;
    };

    // Fetch email notification status
    useEffect(() => {
        if (isCompany) { setNotifLoading(false); return; }
        fetch(`${apiBase}/api/candidate/email-preferences?candidateId=${encodeURIComponent(user.id)}`)
            .then(res => res.json())
            .then(data => setEmailNotifications(data.email_notifications))
            .catch(() => {})
            .finally(() => setNotifLoading(false));
    }, [apiBase, isCompany, user.id]);

    // Toggle email notifications
    const handleToggleNotifications = async () => {
        const newValue = !emailNotifications;
        setNotifLoading(true);
        setNotifSaved(false);
        try {
            const res = await fetch(`${apiBase}/api/candidate/email-preferences`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: user.id, enabled: newValue })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || t('dashboard_modals.account_settings.errors.save_preference_failed'));
            setEmailNotifications(data.email_notifications);
            setNotifSaved(true);
            setTimeout(() => setNotifSaved(false), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setNotifLoading(false);
        }
    };

    // Data export
    const handleExportData = async () => {
        setLoading(true);
        setError('');
        try {
            const endpoint = isCompany 
                ? `${apiBase}/api/company/account/export?companyId=${encodeURIComponent(user.id)}`
                : `${apiBase}/api/candidate/account/export?candidateId=${encodeURIComponent(user.id)}`;

            const res = await fetch(endpoint);
            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.detail || t('dashboard_modals.account_settings.errors.export_failed'));
            }

            setExportData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Download export als JSON
    const handleDownloadExport = () => {
        if (!exportData) return;
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mijn-gegevens-${user.id}-${new Date().toISOString().slice(0,10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Account verwijderen
    const handleDeleteAccount = async () => {
        if (deleteConfirm !== deleteKeyword) {
            setError(t('dashboard_modals.account_settings.errors.type_delete'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            const endpoint = isCompany 
                ? `${apiBase}/api/company/account/delete`
                : `${apiBase}/api/candidate/account/delete`;

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: user.id,
                    password: deletePassword
                })
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.detail || t('dashboard_modals.account_settings.errors.delete_failed'));
            }

            setDeleteSuccess(true);
            // Log uit na 3 seconden
            setTimeout(() => {
                onLogout();
            }, 3000);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
            <div className="w-full max-w-2xl animate-fade-in-up rounded-[1.3rem] border-4 border-[#151827] bg-white shadow-[12px_12px_0_#151827]">
                
                {/* Header */}
                <div className="flex items-center justify-between rounded-t-[1rem] border-b-2 border-[#151827]/20 bg-[#fffdf8] p-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">{t('dashboard.account_privacy')}</h2>
                        <p className="text-sm text-gray-500">{t('dashboard_modals.account_settings.subtitle')}</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:bg-red-50 hover:border-red-200 hover:text-red-500 transition-colors text-xl font-bold text-gray-400"
                    >
                        ✕
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                    <button 
                        onClick={() => setActiveTab('privacy')}
                                className={`flex-1 py-4 text-sm font-medium transition ${activeTab === 'privacy' ? 'border-b-2 border-[#3f7f65] text-[#3f7f65]' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {t('dashboard_modals.account_settings.tabs.privacy')}
                    </button>
                    {!isCompany && (
                        <button 
                            onClick={() => setActiveTab('notifications')}
                            className={`flex-1 py-4 text-sm font-medium transition ${activeTab === 'notifications' ? 'border-b-2 border-[#3f7f65] text-[#3f7f65]' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {t('dashboard_modals.account_settings.tabs.notifications')}
                        </button>
                    )}
                    <button 
                        onClick={() => setActiveTab('export')}
                        className={`flex-1 py-4 text-sm font-medium transition ${activeTab === 'export' ? 'border-b-2 border-[#3f7f65] text-[#3f7f65]' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {t('dashboard_modals.account_settings.tabs.data')}
                    </button>
                    <button 
                        onClick={() => setActiveTab('delete')}
                        className={`flex-1 py-4 text-sm font-medium transition ${activeTab === 'delete' ? 'text-red-500 border-b-2 border-red-500' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {t('dashboard_modals.account_settings.tabs.delete')}
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    {/* NOTIFICATIONS TAB */}
                    {activeTab === 'notifications' && (
                        <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                                <h3 className="font-bold text-blue-800 mb-2">{t('dashboard_modals.account_settings.notifications.title')}</h3>
                                <p className="text-sm text-blue-700">
                                    {t('dashboard_modals.account_settings.notifications.subtitle')}
                                </p>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between">
                                <div>
                                    <h4 className="font-semibold text-gray-900">{t('dashboard_modals.account_settings.notifications.match_notifications')}</h4>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {emailNotifications 
                                            ? t('dashboard_modals.account_settings.notifications.enabled') 
                                            : t('dashboard_modals.account_settings.notifications.disabled')}
                                    </p>
                                </div>
                                <button
                                    onClick={handleToggleNotifications}
                                    disabled={notifLoading}
                                    className={`relative w-14 h-7 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2 ${
                                        emailNotifications ? 'bg-brand-green' : 'bg-gray-300'
                                    } ${notifLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200 ${
                                        emailNotifications ? 'translate-x-7' : 'translate-x-0'
                                    }`} />
                                </button>
                            </div>

                            {notifSaved && (
                                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                                    {t('dashboard_modals.account_settings.notifications.saved')}
                                </div>
                            )}
                        </div>
                    )}

                    {/* PRIVACY INFO TAB */}
                    {activeTab === 'privacy' && (
                        <div className="space-y-4">
                            <div className="bg-white border border-gray-200 rounded-xl p-5">
                                <h3 className="font-bold text-gray-900 mb-2">{t('dashboard.language_preference')}</h3>
                                <p className="text-sm text-gray-500 mb-4">{t('dashboard.language_preference_hint')}</p>
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <LanguageSwitcher variant="labeled" onPersist={persistLanguagePreference} />
                                    {languageSaved && (
                                        <span className="text-sm font-medium text-green-700">✓ {t('dashboard.language_saved')}</span>
                                    )}
                                </div>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                                <h3 className="font-bold text-blue-800 mb-2">{t('dashboard_modals.account_settings.privacy.rights_title')}</h3>
                                <ul className="text-sm text-blue-700 space-y-2">
                                    <li>{t('dashboard_modals.account_settings.privacy.right_1')}</li>
                                    <li>{t('dashboard_modals.account_settings.privacy.right_2')}</li>
                                    <li>{t('dashboard_modals.account_settings.privacy.right_3')}</li>
                                    <li>{t('dashboard_modals.account_settings.privacy.right_4')}</li>
                                </ul>
                            </div>

                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                                <h3 className="font-bold text-gray-800 mb-2">{t('dashboard_modals.account_settings.privacy.usage_title')}</h3>
                                <ul className="text-sm text-gray-600 space-y-2">
                                    <li>{t('dashboard_modals.account_settings.privacy.usage_1', { target: isCompany ? t('dashboard_modals.shared.candidates') : t('dashboard_modals.shared.employers') })}</li>
                                    <li>{t('dashboard_modals.account_settings.privacy.usage_2')}</li>
                                    <li>{t('dashboard_modals.account_settings.privacy.usage_3')}</li>
                                    <li>{t('dashboard_modals.account_settings.privacy.usage_4')}</li>
                                </ul>
                            </div>

                            <div className="flex gap-3">
                                <a 
                                    href={WEREPRESENT_LINKS.privacy}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 text-center bg-gray-100 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-200 transition"
                                >
                                    {t('dashboard_modals.account_settings.privacy.view_privacy')}
                                </a>
                                <a 
                                    href={WEREPRESENT_LINKS.voorwaarden}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 text-center bg-gray-100 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-200 transition"
                                >
                                    {t('dashboard_modals.account_settings.privacy.view_terms')}
                                </a>
                                <a 
                                    href={WEREPRESENT_LINKS.aiPolicy}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 text-center bg-gray-100 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-gray-200 transition"
                                >
                                    {t('dashboard_modals.account_settings.privacy.view_ai_policy')}
                                </a>
                            </div>
                        </div>
                    )}

                    {/* DATA EXPORT TAB */}
                    {activeTab === 'export' && (
                        <div className="space-y-4">
                            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                                <h3 className="font-bold text-green-800 mb-2">{t('dashboard_modals.account_settings.export.title')}</h3>
                                <p className="text-sm text-green-700">
                                    {t('dashboard_modals.account_settings.export.subtitle')}
                                </p>
                            </div>

                            {!exportData ? (
                                <button
                                    onClick={handleExportData}
                                    disabled={loading}
                                    className="w-full rounded-xl bg-[#3f7f65] px-6 py-4 font-bold text-white transition hover:opacity-90 disabled:opacity-50"
                                >
                                    {loading ? t('dashboard_modals.account_settings.export.loading') : t('dashboard_modals.account_settings.export.request_data')}
                                </button>
                            ) : (
                                <div className="space-y-4">
                                    <div className="bg-white border border-gray-200 rounded-xl p-4 max-h-64 overflow-y-auto">
                                        <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                                            {JSON.stringify(exportData, null, 2).slice(0, 2000)}
                                            {JSON.stringify(exportData, null, 2).length > 2000 && `\n... ${t('dashboard_modals.account_settings.export.more_in_download')}`}
                                        </pre>
                                    </div>
                                    <button
                                        onClick={handleDownloadExport}
                                        className="w-full rounded-xl bg-[#3f7f65] px-6 py-4 font-bold text-white transition hover:opacity-90"
                                    >
                                        {t('dashboard_modals.account_settings.export.download_json')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* DELETE ACCOUNT TAB */}
                    {activeTab === 'delete' && (
                        <div className="space-y-4">
                            {deleteSuccess ? (
                                <div className="text-center py-8">
                                    <div className="text-6xl mb-4">👋</div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">{t('dashboard_modals.account_settings.delete.deleted_title')}</h3>
                                    <p className="text-gray-500">{t('dashboard_modals.account_settings.delete.deleted_body')}</p>
                                </div>
                            ) : !showDeleteConfirm ? (
                                <>
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                        <h3 className="font-bold text-red-800 mb-2">{t('dashboard_modals.account_settings.delete.warning_title')}</h3>
                                        <p className="text-sm text-red-700">
                                            {t('dashboard_modals.account_settings.delete.warning_body')}
                                        </p>
                                    </div>

                                    <button
                                        onClick={() => setShowDeleteConfirm(true)}
                                        className="w-full bg-red-500 text-white px-6 py-4 rounded-xl font-bold hover:bg-red-600 transition"
                                    >
                                        {t('dashboard_modals.account_settings.delete.start_button')}
                                    </button>
                                </>
                            ) : (
                                <div className="space-y-4">
                                    <div className="bg-red-100 border border-red-300 rounded-xl p-4">
                                        <h3 className="font-bold text-red-800 mb-2">{t('dashboard_modals.account_settings.delete.confirm_title')}</h3>
                                        <p className="text-sm text-red-700">
                                            {t('dashboard_modals.account_settings.delete.confirm_body')}
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('dashboard_modals.account_settings.delete.password_label')}
                                        </label>
                                        <input
                                            type="password"
                                            value={deletePassword}
                                            onChange={(e) => setDeletePassword(e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                                            placeholder={t('dashboard_modals.account_settings.delete.password_placeholder')}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('dashboard_modals.account_settings.delete.type_delete_label')}
                                        </label>
                                        <input
                                            type="text"
                                            value={deleteConfirm}
                                            onChange={(e) => setDeleteConfirm(e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                                            placeholder={t('dashboard_modals.account_settings.delete.type_delete_placeholder')}
                                        />
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => {
                                                setShowDeleteConfirm(false);
                                                setDeletePassword('');
                                                setDeleteConfirm('');
                                                setError('');
                                            }}
                                            className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-200 transition"
                                        >
                                            {t('common.cancel')}
                                        </button>
                                        <button
                                            onClick={handleDeleteAccount}
                                            disabled={loading || deleteConfirm !== deleteKeyword}
                                            className="flex-1 bg-red-500 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-600 transition disabled:opacity-50"
                                        >
                                            {loading ? t('dashboard_modals.account_settings.delete.deleting') : t('dashboard_modals.account_settings.delete.delete_forever')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// --- CONTACT MODAL ---
function ContactModal({ user, apiBase, isCompany, onClose }) {
    const { t } = useTranslation();
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const supportEmail = 'info@betaaldsolliciteren.nl';
    const mailSubjectBrand = isCompany ? 'werepresenttalent.nl' : 'Betaaldsolliciteren.nl';
    const userName = user.personalia?.name || user.username || 'Gebruiker';
    const userEmail = user.email || user.personalia?.email || '';

    const handleSend = async () => {
        if (!subject.trim() || !message.trim()) {
            setError(t('dashboard_modals.contact.errors.fill_subject_and_message'));
            return;
        }

        setSending(true);
        setError('');

        try {
            // Stuur email via backend
            const res = await fetch(`${apiBase}/api/contact/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: user.id,
                    userType: isCompany ? 'company' : 'candidate',
                    userName,
                    userEmail,
                    subject: subject.trim(),
                    message: message.trim()
                })
            });

            if (res.ok) {
                setSent(true);
            } else {
                const data = await res.json();
                setError(data.detail || t('dashboard_modals.contact.errors.send_failed'));
            }
        } catch (e) {
            console.error('Contact error:', e);
            setError(t('dashboard_modals.contact.errors.generic_with_email', { email: supportEmail }));
        } finally {
            setSending(false);
        }
    };

    // Succes scherm
    if (sent) {
        return (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                <div className="w-full max-w-md overflow-hidden rounded-[1.3rem] border-4 border-[#151827] bg-white shadow-[12px_12px_0_#151827] animate-fade-in-up">
                    <div className="p-8 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-3xl">✓</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('dashboard_modals.contact.success_title')}</h2>
                        <p className="text-gray-600 mb-6">
                            {t('dashboard_modals.contact.success_body')}
                        </p>
                        <button
                            onClick={onClose}
                            className="w-full rounded-xl bg-[#3f7f65] px-6 py-3 font-bold text-white transition hover:opacity-90"
                        >
                            {t('common.close')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
            <div className="my-8 w-full max-w-lg overflow-hidden rounded-[1.3rem] border-4 border-[#151827] bg-white shadow-[12px_12px_0_#151827] animate-fade-in-up">
                {/* Header */}
                <div className="border-b-2 border-[#151827]/20 bg-[#fffdf8] p-6 text-[#151827]">
                    <div className="flex justify-between items-start">
                        <div>
                            <h2 className="text-xl font-bold">{t('dashboard.contact')}</h2>
                            <p className="mt-1 text-sm text-[#151827]/70">
                                {t('dashboard_modals.contact.subtitle')}
                            </p>
                        </div>
                        <button 
                            onClick={onClose}
                            className="p-2 hover:bg-white/20 rounded-lg transition touch-target"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                            {error}
                        </div>
                    )}

                    {/* Quick contact info */}
                    <div className="flex items-center gap-3 rounded-xl border border-[#151827]/15 bg-[#f6f1e8] p-4">
                        <div className="h-10 w-10 flex-shrink-0 rounded-full border border-[#3f7f65]/25 bg-[#e9f4ef] flex items-center justify-center">
                            <span className="text-lg">📧</span>
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs text-gray-500 uppercase font-medium">{t('dashboard_modals.contact.direct_mail')}</div>
                            <a 
                                href={`mailto:${supportEmail}?subject=Contact via ${mailSubjectBrand}`}
                                className="break-all font-medium text-[#3f7f65] hover:underline"
                            >
                                {supportEmail}
                            </a>
                        </div>
                    </div>

                    {isCompany && (
                        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-xl text-sm">
                            {t('dashboard_modals.contact.company_hint')}
                        </div>
                    )}

                    {/* Subject */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">
                            {t('dashboard_modals.contact.subject_label')} <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="min-h-[44px] w-full rounded-lg border-2 border-[#151827]/25 bg-white px-4 py-3 transition focus:border-[#3f7f65] focus:outline-none"
                        >
                            <option value="">{t('dashboard_modals.contact.subject_placeholder')}</option>
                            <option value={t('dashboard_modals.contact.subjects.onboarding')}>{t('dashboard_modals.contact.subjects.onboarding')}</option>
                            <option value={t('dashboard_modals.contact.subjects.account')}>{t('dashboard_modals.contact.subjects.account')}</option>
                            <option value={t('dashboard_modals.contact.subjects.match')}>{t('dashboard_modals.contact.subjects.match')}</option>
                            <option value={t('dashboard_modals.contact.subjects.technical')}>{t('dashboard_modals.contact.subjects.technical')}</option>
                            <option value={t('dashboard_modals.contact.subjects.feedback')}>{t('dashboard_modals.contact.subjects.feedback')}</option>
                            <option value={t('dashboard_modals.contact.subjects.other')}>{t('dashboard_modals.contact.subjects.other')}</option>
                        </select>
                    </div>

                    {/* Message */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">
                            {t('dashboard_modals.contact.message_label')} <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={5}
                            placeholder={t('dashboard_modals.contact.message_placeholder')}
                            className="w-full resize-none rounded-lg border-2 border-[#151827]/25 px-4 py-3 transition focus:border-[#3f7f65] focus:outline-none"
                        />
                        <div className="text-xs text-gray-400 mt-1 text-right">
                            {t('dashboard_modals.contact.characters_counter', { count: message.length })}
                        </div>
                    </div>

                    {/* From info */}
                    <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                        <span className="text-gray-400">{t('dashboard_modals.contact.from_label')}</span> {userName} {userEmail && `(${userEmail})`}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 pb-6 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-bold hover:bg-gray-200 transition"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={sending || !subject || !message.trim()}
                        className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#3f7f65] px-6 py-3 font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {sending ? (
                            <>
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                </svg>
                                {t('dashboard_modals.contact.sending')}
                            </>
                        ) : (
                            <>
                                <span>{t('common.submit')}</span>
                                <span>→</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}